/**
 * DraftCandidatesPage — Review-Schritt für automatisch generierte Entwürfe.
 *
 * Der lokale Python-Renderer (Projekt "Personalization Tool") schreibt
 * Kandidaten nach `draft_candidates` und lädt Bilder nach Storage
 * `candidates/`. Diese Seite ist der menschliche Review-Schritt davor,
 * dass ein Entwurf an den Kunden geht.
 *
 * Freigabe reicht den Kandidaten in den BESTEHENDEN Kundenpfad ein: sie legt
 * ein `drafts/{candidateId}`-Doc mit exakt denselben Feldern an, die
 * AdminUploadPage schreibt. Dadurch feuert `sendCustomerEmail` genau wie
 * heute — keine Cloud-Function-Änderung nötig.
 *
 * Idempotenz: die Draft-Doc-ID IST die Candidate-ID. Es kann per Definition
 * keine zwei Drafts für einen Kandidaten geben. Zusätzlich prüft die
 * Transaktion, dass der Kandidat noch `pending_review` ist.
 *
 * Versionsketten: der Renderer legt bei jeder Korrekturrunde einen NEUEN
 * Kandidaten an (Doc-ID `{orderId}_{transactionId}_{timestamp}`) und setzt
 * die Vorgänger auf `superseded`. Diese Seite fasst eine Kette zusammen: der
 * aktuelle Kandidat ist die Karte, die Vorgänger liegen aufklappbar darunter
 * und können einzeln gelöscht werden — nur das Firestore-Dokument; die
 * Bilder unter `candidates/` gehören dem Personalization Tool und werden
 * dort aufgeräumt. Siehe docs/housekeeping.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection, doc, deleteDoc, getDoc, onSnapshot, query, where,
  runTransaction, serverTimestamp, setDoc, updateDoc, addDoc, Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
// `storage` wird hier nicht mehr gebraucht: Seit 14.09.2026 loescht diese
// Seite keine Dateien mehr — siehe handleDeleteCandidate.
import { db } from "../firebase";

// ── Farbtokens (konsistent mit ReportingPage / OrdersPage) ────────────────
const C = {
  cardBg:      "#ffffff",
  border:      "#e2e8f0",
  textStrong:  "#2d3748",
  textMuted:   "#718096",
  textLight:   "#a0aec0",
  primaryDark: "#5a4a44",
  green:       "#68a37a",
  greenSoft:   "#c6f6d5",
  amber:       "#e6a545",
  amberSoft:   "#fff8e1",
  red:         "#c76d5a",
  redSoft:     "#fed7d7",
  greySoft:    "#f7fafc",
  blueSoft:    "#ebf4ff",
};

const STAGE_LABELS = {
  new: "Neu", grafiker: "Grafiker", entwurf_raus: "Entwurf raus",
  produktion: "Produktion", druck: "Druck", kerzen: "Kerzen",
  archived: "Archiviert",
};

// Stages ab denen der Kunde die Bestelldetails bestätigt hat.
const STAGE_OK_FOR_SEND = new Set([
  "grafiker", "entwurf_raus", "produktion", "druck", "kerzen",
]);

const VALUE_LABELS = {
  name: "Name", datum: "Datum", spruch: "Spruch",
  bibelstelle: "Bibelstelle", anlass: "Anlass", notes: "Anmerkungen",
};
const VALUE_ORDER = ["name", "datum", "spruch", "bibelstelle", "anlass", "notes"];

const fmtTs = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

/** Millisekunden aus Timestamp | Date | Zahl | ISO-String; 0 wenn unbekannt.
 *  createdAt kann null sein solange der serverTimestamp noch pending ist. */
const tsMillis = (ts) => {
  if (!ts) return 0;
  try {
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (typeof ts === "number") return ts;
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch { return 0; }
};

/**
 * Macht aus beliebigem Firestore-Wert etwas, das React rendern darf.
 * Der Renderer schreibt z. B. renderedBy als {component, commit} — ein
 * Objekt als React-Child wirft React #31 und reisst die ganze Seite mit.
 */
const safeText = (v) => {
  if (v === null || v === undefined) return "";
  const t = typeof v;
  if (t === "string") return v;
  if (t === "number" || t === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(safeText).filter(Boolean).join(", ");
  if (t === "object") {
    if (typeof v.toDate === "function") return fmtTs(v);
    // Bevorzugt sprechende Felder, sonst kompakte key=value-Liste
    const preferred = ["name", "label", "title", "component", "value"];
    for (const k of preferred) {
      if (typeof v[k] === "string" && v[k]) {
        const rest = Object.entries(v)
          .filter(([kk, vv]) => kk !== k && vv !== null && vv !== undefined &&
            typeof vv !== "object")
          .map(([kk, vv]) => `${kk}=${vv}`);
        return rest.length ? `${v[k]} (${rest.join(", ")})` : v[k];
      }
    }
    try {
      return Object.entries(v)
        .filter(([, vv]) => vv !== null && vv !== undefined && typeof vv !== "object")
        .map(([kk, vv]) => `${kk}=${vv}`)
        .join(", ");
    } catch { return ""; }
  }
  return "";
};

/**
 * Normalisiert ein rohes draft_candidates-Doc auf eine stabile Form.
 * Der Renderer weicht in mehreren Punkten vom ursprünglichen Contract ab
 * (verschachtelte preview/print-Objekte, belowReference als Liste,
 * numerische IDs, Zusatzfelder). Statt überall Fallbacks zu streuen wird
 * hier einmal zentral vereinheitlicht — beide Formen bleiben lesbar.
 */
/**
 * Deutet die Antwort des Watchers auf eine Erzwingen-Anfrage.
 *
 * `forceAutoResult` ist entweder `"Entwurf <id>"` oder `"abgelehnt: <Grund>"`.
 * Der Grund wird immer woertlich angezeigt; nur der erklaerende Zusatz zum
 * Image Generator haengt davon ab, worum es geht. Frueher stand er unter jeder
 * Ablehnung — bei „abgelehnt: Bestellung nicht gefunden" hat er in die Irre
 * gefuehrt (Nachtrag des Tools vom 11.09.).
 *
 * @param {*} result - forceAutoResult, wie der Watcher es geschrieben hat
 * @return {"none"|"ok"|"rejected"|"rejected_variant"} Art der Antwort
 */
export const forceAutoResultKind = (result) => {
  const text = safeText(result).trim();
  if (!text) return "none";
  if (!/^abgelehnt/i.test(text)) return "ok";
  return /^abgelehnt\s*:\s*ausführung/i.test(text)
    ? "rejected_variant" : "rejected";
};

/**
 * Liest aus `forcedReason` heraus, welche Pflichtwerte beim Erzwingen durch
 * `xxxxx` ersetzt wurden.
 *
 * Der Watcher meldet das als eigenes Segment, z. B.
 *   "Etsy-Feld nicht zugeordnet: 'Personalisierung' · Freitext geparst
 *    · Platzhalter: name, datum"
 *
 * Ein solcher Entwurf darf den Kunden nicht erreichen — auf dem Bild stünde
 * wörtlich "xxxxx". Die Erkennung ist absichtlich grosszuegig: einen Entwurf
 * faelschlich zu sperren kostet einen Klick im Schnellfix, ein uebersehener
 * Platzhalter kostet eine E-Mail an die Kundin, die niemand zurueckholen kann.
 *
 * @param {*} reason - forcedReason, wie der Watcher es geschrieben hat
 * @return {Array<string>} benannte Platzhalter, leer wenn keine
 */
export const forcedPlaceholderList = (reason) => {
  const text = safeText(reason);
  if (!text) return [];
  // Segmente sind durch "·" getrennt; Fallback auf die ganze Zeile, falls
  // der Watcher das Trennzeichen einmal anders setzt.
  const segments = text.split("·").map((s) => s.trim()).filter(Boolean);
  const hits = [];
  for (const seg of segments.length ? segments : [text]) {
    const m = seg.match(/platzhalter\s*:?\s*(.*)$/i);
    if (!m) continue;
    const listed = m[1]
        .split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    // "Platzhalter" ohne Aufzaehlung zaehlt trotzdem als Treffer.
    hits.push(...(listed.length ? listed : ["unbenannt"]));
  }
  return hits;
};

/**
 * Zweite, unabhaengige Erkennung: steht das woertliche xxxxx in den Werten,
 * die gerendert wurden? Greift auch dann, wenn der Watcher den Grund einmal
 * anders formuliert als erwartet.
 *
 * @param {*} values - das values-Objekt des Kandidaten
 * @return {Array<string>} Feldnamen mit xxxxx-Wert
 */
export const placeholderValueFields = (values) => {
  if (!values || typeof values !== "object" || Array.isArray(values)) return [];
  return Object.entries(values)
      .filter(([, v]) => /^x{4,}$/i.test(String(v ?? "").trim()))
      .map(([k]) => k);
};

/**
 * styleSwitch ist rein informativ: der Renderer musste den Spruch auf die
 * Alternativschrift umstellen, weil er zu lang war. Der Contract steht noch
 * nicht endgueltig fest, deshalb werden mehrere Feldnamen akzeptiert.
 * Ergebnis z. B.:
 *   "Schrift gewechselt: Madore → Carrois Gothic SC (187 Zeichen, Schwelle 127)"
 */
const styleSwitchLine = (sw) => {
  if (!sw || typeof sw !== "object" || Array.isArray(sw)) return "";
  const pick = (...keys) => {
    for (const k of keys) {
      const v = sw[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return null;
  };
  const from = pick("from", "fromFont", "fromStyle", "original", "primary");
  const to = pick("to", "toFont", "toStyle", "alternative", "fallback");
  if (!from && !to) return "";

  const chars = pick("chars", "charCount", "length", "textLength", "len");
  const limit = pick("threshold", "limit", "maxChars", "max");

  let line = `Schrift gewechselt: ${safeText(from) || "?"} → ${safeText(to) || "?"}`;
  const detail = [];
  if (chars !== null) detail.push(`${safeText(chars)} Zeichen`);
  if (limit !== null) detail.push(`Schwelle ${safeText(limit)}`);
  if (detail.length) line += ` (${detail.join(", ")})`;
  return line;
};

/**
 * referenceThumbs des Renderers auf eine sichere Form bringen:
 * [{url, storagePath, label, matchedOn[]}], hoechstens 10, ohne Eintraege
 * ohne Bild-URL. matchedOn wird immer zu einem String-Array.
 */
const normalizeThumbs = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((t) => {
      if (!t || typeof t !== "object") return null;
      const url = typeof t.url === "string" ? t.url.trim() : "";
      if (!url) return null;
      const m = t.matchedOn;
      const matchedOn = Array.isArray(m)
        ? m.map((x) => safeText(x)).filter((x) => x.trim() !== "")
        : (safeText(m).trim() ? [safeText(m).trim()] : []);
      return {
        url,
        storagePath: typeof t.storagePath === "string" ? t.storagePath : null,
        label: safeText(t.label).trim(),
        matchedOn,
      };
    })
    .filter(Boolean)
    .slice(0, 10);
};

/**
 * Freigabe-Bedingungen. Eine einzige Quelle, damit Karte und Pruefansicht
 * garantiert dieselbe Sperre und denselben Hinweistext zeigen.
 */
export const computeGating = (cand, order, orderLoading) => {
  const stage = order?.workflowStage || (order ? "new" : null);
  const hasEmail = !!order?.customerEmail;
  const stageOk = stage ? STAGE_OK_FOR_SEND.has(stage) : false;
  const orderMissing = !orderLoading && !order;

  // Ein erzwungener Entwurf kann xxxxx im Bild tragen, wo Name oder Datum
  // fehlten. Der geht nicht raus: auf dem Entwurf, den die Kundin per E-Mail
  // bekommt, stuende woertlich "xxxxx". Erst im Schnellfix ersetzen, dann
  // gibt der Watcher einen neuen Kandidaten ohne Platzhalter frei.
  const placeholders = Array.isArray(cand.forcedPlaceholders)
    ? cand.forcedPlaceholders : [];
  const hasPlaceholders = placeholders.length > 0;

  const canSend = cand.status === "pending_review" && stageOk && hasEmail
    && !orderMissing && !hasPlaceholders;

  let disabledHint = null;
  if (cand.status !== "pending_review") {
    disabledHint = "Kandidat ist nicht mehr in Prüfung.";
  } else if (hasPlaceholders) {
    const named = placeholders.filter((p) => p !== "unbenannt");
    disabledHint = "Enthält noch Platzhalter (xxxxx)" +
      (named.length ? ` für: ${named.join(", ")}` : "") +
      " — bitte über „Bearbeiten (lokal)“ ersetzen. " +
      "Danach kommt ein neuer Kandidat ohne Platzhalter.";
  } else if (orderLoading) {
    disabledHint = "Bestelldaten werden geladen…";
  } else if (orderMissing) {
    disabledHint = `Bestellung ${cand.orderId} nicht in Firestore gefunden.`;
  } else if (!stageOk && !hasEmail) {
    disabledHint = "Kunde hat die Bestelldetails noch nicht bestätigt " +
      `(Stage: ${STAGE_LABELS[stage] || stage}) und es ist noch keine ` +
      "E-Mail-Adresse hinterlegt (Scraper noch nicht gelaufen).";
  } else if (!stageOk) {
    disabledHint = "Kunde hat die Bestelldetails noch nicht bestätigt " +
      `(Stage: ${STAGE_LABELS[stage] || stage} — nötig: Grafiker oder später).`;
  } else if (!hasEmail) {
    disabledHint = "Noch keine E-Mail-Adresse an der Bestellung " +
      "(Scraper hat sie noch nicht ergänzt).";
  }

  return { stage, hasEmail, stageOk, orderMissing, canSend, disabledHint,
    hasPlaceholders, placeholders };
};

/**
 * Wurde dieser Kandidat im Schnellfix tatsaechlich korrigiert?
 *
 * Massgeblich sind echte Korrektureintraege in `overrides`, NICHT die
 * Herkunft: Stand 14.09.2026 hatten zwei Kandidaten einen
 * `parentCandidateId`, aber keine einzige Korrektur (neu gerendert, nichts
 * veraendert). Ein `carried`-Eintrag zaehlt nicht — das ist eine vom
 * Vorgaenger uebernommene Einstellung, keine Handarbeit dieses Durchgangs.
 * Die alte Zuordnungsform ({Umbruch: true, …}) wird ebenfalls gelesen.
 */
export const hasRealCorrections = (overrides) => {
  if (Array.isArray(overrides)) {
    return overrides.some((o) => {
      if (typeof o === "string" || typeof o === "number") return true;
      if (!o || typeof o !== "object") return false;
      const kind = safeText(o.kind ?? o.label ?? o.type ?? o.name ?? "")
        .trim().toLowerCase();
      return kind !== "carried";
    });
  }
  if (overrides && typeof overrides === "object") {
    return Object.entries(overrides).some(([k, v]) =>
      k !== "advanced" && k.toLowerCase() !== "carried" &&
      v !== false && v !== null && v !== undefined && v !== "");
  }
  return false;
};

export const normalizeCandidate = (raw) => {
  const preview = (raw.preview && typeof raw.preview === "object") ? raw.preview : {};
  const printObj = (raw.print && typeof raw.print === "object") ? raw.print : {};

  // belowReference: bool ODER Liste betroffener Layer
  const brRaw = raw.belowReference;
  const belowReferenceLayers = Array.isArray(brRaw) ? brRaw.map(safeText).filter(Boolean) : [];
  const belowReference = Array.isArray(brRaw) ? brRaw.length > 0 : !!brRaw;

  // Gründe für manuelle Bearbeitung: manualReason (String) und/oder reasons (Liste)
  const reasonList = Array.isArray(raw.reasons)
    ? raw.reasons.map(safeText).filter(Boolean) : [];
  const manualReason = safeText(raw.manualReason);

  // needs_manual kann über status ODER das bool-Feld kommen
  const status = typeof raw.status === "string" ? raw.status : "pending_review";
  const effectiveStatus =
    (status === "pending_review" && raw.needsManual === true) ? "needs_manual" : status;

  return {
    ...raw,
    status: effectiveStatus,
    rawStatus: status,
    orderId: raw.orderId != null ? String(raw.orderId) : "",
    transactionId: raw.transactionId != null ? String(raw.transactionId) : "",
    listingId: raw.listingId != null ? String(raw.listingId) : "",
    templateId: raw.templateId != null ? String(raw.templateId) : "",
    templateName: safeText(raw.templateName),
    previewUrl: raw.previewUrl || preview.url || null,
    previewStoragePath: raw.previewStoragePath || preview.path || null,
    printUrl: raw.printUrl || printObj.url || null,
    printStoragePath: raw.printStoragePath || printObj.path || null,
    belowReference,
    belowReferenceLayers,
    manualReason,
    reasonList,
    renderedByText: safeText(raw.renderedBy),
    reviewNote: safeText(raw.reviewNote),
    valuesObj: (raw.values && typeof raw.values === "object" && !Array.isArray(raw.values))
      ? raw.values : null,
    fitReportList: Array.isArray(raw.fitReport) ? raw.fitReport : [],
    cropMode: (raw.crop && typeof raw.crop === "object") ? safeText(raw.crop.mode) : "",
    createdAtMs: tsMillis(raw.createdAt),
    modifiedAtMs: tsMillis(raw.modifiedAt),
    // Referenzbilder: freigegebene Drucke der Grafikerin, die der Renderer
    // als aehnlich ausgewaehlt hat. NUR fuer die interne Pruefansicht —
    // sie zeigen Arbeiten anderer Kunden und duerfen weder auf der
    // Kundenseite noch in einer E-Mail auftauchen.
    referenceThumbs: normalizeThumbs(raw.referenceThumbs),
    // Korrekturen aus dem lokalen Editor, als fertige Anzeigezeile.
    overridesText: overridesLine(raw.overrides),
    // Warum der Kandidat hinfaellig wurde (Renderer oder Shop-Tool).
    // Roh der Schluessel, dazu die deutsche Beschriftung fuer die Anzeige.
    obsoleteReason: safeText(raw.obsoleteReason).trim(),
    obsoleteReasonLabel: obsoleteReasonLabel(raw.obsoleteReason),
    overridesAdvanced: overridesHasAdvanced(raw.overrides),
    // Aus dem Vorgaenger geerbte Einstellungen — eigene Zeile, siehe oben.
    carriedText: carriedLine(raw.overrides),
    // Explizite Verkettung durch den Editor; leer bei Erstentwuerfen.
    parentCandidateId: (typeof raw.parentCandidateId === "string"
      && raw.parentCandidateId.trim() !== "")
      ? raw.parentCandidateId.trim() : null,
    // Hinweis des Renderers an den Kunden (z. B. gewechselte Schrift).
    // Wandert bei der Freigabe in den Draft und in die erste E-Mail.
    customerNotice: safeText(raw.customerNotice).trim(),
    styleSwitchText: styleSwitchLine(raw.styleSwitch),
    // Drive-Ablage (vom Scraper nach Kundenfreigabe gesetzt)
    driveUploadedAtMs: tsMillis(raw.driveUploadedAt),
    driveFileName: safeText(raw.driveFileName),
    driveUploadError: safeText(raw.driveUploadError),

    // ── „Auto-Entwurf erzwingen" ─────────────────────────────────────────
    // Anforderung an den Personalization-Watcher. Die Review-App setzt nur
    // forceAutoPending + forceAuto und liest, was zurueckkommt; gerendert
    // und der Status gesetzt wird ausschliesslich dort.
    forceAutoPending: raw.forceAutoPending === true,
    forceAutoBy: safeText(raw.forceAuto && raw.forceAuto.by),
    forceAutoRequestedAtMs: tsMillis(raw.forceAuto && raw.forceAuto.requestedAt),
    forceAutoResult: safeText(raw.forceAutoResult).trim(),
    forceAutoHandledAtMs: tsMillis(raw.forceAutoHandledAt),
    // Merkmale des erzwungenen Entwurfs selbst. Auf gewoehnlichen
    // Kandidaten fehlen sie — dann false/leer.
    forced: raw.forced === true,
    forcedReason: safeText(raw.forcedReason).trim(),
    forcedBy: safeText(raw.forcedBy).trim(),
    // Der Watcher setzt fehlende Pflichtwerte als xxxxx ins Bild und meldet
    // das in forcedReason ("Platzhalter: name, datum"). So ein Entwurf darf
    // den Kunden nicht erreichen — computeGating sperrt die Freigabe.
    forcedPlaceholders: Array.from(new Set([
      ...forcedPlaceholderList(raw.forcedReason),
      ...placeholderValueFields(raw.values),
    ])),
  };
};

/**
 * Fängt Renderfehler einer einzelnen Karte ab, damit ein einzelnes
 * fehlerhaftes Dokument nie die ganze Seite weisst.
 */
class CardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("draft_candidates card render failed",
      this.props.candidateId, error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          background: "#fff", border: "1px solid #fc8181", borderRadius: 10,
          padding: "0.9em 1.1em", color: "#742a2a", fontSize: "0.85rem",
        }}>
          <div style={{ fontWeight: 700, marginBottom: "0.3em" }}>
            Dieser Kandidat konnte nicht dargestellt werden
          </div>
          <div style={{ fontSize: "0.8rem" }}>
            Dokument: <code>{this.props.candidateId}</code>
            {this.props.orderId && <> · Bestellung #{this.props.orderId}</>}
          </div>
          <div style={{
            marginTop: "0.4em", fontSize: "0.76rem", color: "#975a16",
            fontFamily: "monospace",
          }}>{String(this.state.error?.message || this.state.error)}</div>
          <div style={{ marginTop: "0.4em", fontSize: "0.76rem", color: C.textMuted }}>
            Die übrigen Kandidaten sind davon nicht betroffen.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
};

// ── Kleine UI-Bausteine ───────────────────────────────────────────────────

const Badge = ({ children, bg, color, border, title }) => (
  <span title={title} style={{
    display: "inline-block", padding: "0.15em 0.6em", borderRadius: 999,
    fontSize: "0.72rem", fontWeight: 600, background: bg, color,
    border: `1px solid ${border || bg}`, whiteSpace: "nowrap",
  }}>{children}</span>
);

const STATUS_BADGE = {
  pending_review: { label: "Prüfung offen", bg: C.amberSoft, color: "#7b341e", border: "#f6ad55" },
  approved:       { label: "An Kunden gesendet", bg: C.greenSoft, color: "#22543d", border: "#68d391" },
  rejected:       { label: "Abgelehnt",     bg: C.redSoft,   color: "#742a2a", border: "#fc8181" },
  needs_manual:   { label: "Manuell nötig", bg: C.blueSoft,  color: "#2a4365", border: "#90cdf4" },
  superseded:     { label: "Ersetzt",       bg: C.greySoft,  color: C.textMuted, border: C.border },
  obsolete:       { label: "Hinfällig",     bg: C.greySoft,  color: C.textMuted, border: C.border },
};

/**
 * Fit-Report des Renderers.
 * Contract: Array [{layer, status, fontPx, lines, shrinkFactor}]
 * status ∈ fit | fit_wrapped | fit_shrunk | fit_shrunk_below_reference | needs_manual
 */
const FIT_STATUS = {
  skipped:                    { label: "nicht belegt",       color: C.textLight, weight: -1 },
  fit:                        { label: "passt",              color: C.green,     weight: 0 },
  fit_wrapped:                { label: "umgebrochen",        color: C.green,     weight: 1 },
  fit_shrunk:                 { label: "verkleinert",        color: C.amber,     weight: 2 },
  fit_shrunk_below_reference: { label: "unter Referenz",     color: C.amber,     weight: 3 },
  needs_manual:               { label: "manuell nötig",      color: C.red,       weight: 4 },
  // Der Editor hat die Ebene festgesetzt; sie wurde bewusst nicht mehr
  // eingepasst. Neutral: kein Mangel, deshalb Gewicht 0 wie "passt".
  pinned:                     { label: "festgesetzt",        color: C.textMuted, weight: 0 },
};

const fmtShrink = (f) => {
  if (f === null || f === undefined || f === "") return "—";
  const n = Number(f);
  if (!Number.isFinite(n)) return String(f);
  if (n >= 1) return "—";                       // nicht verkleinert
  return `${Math.round(n * 100)} %`;
};

const FitReport = ({ fitReport, styleSwitch, belowLayers }) => {
  const rows = Array.isArray(fitReport) ? fitReport : [];
  if (rows.length === 0 && !styleSwitch) return null;

  // Auffälligster Status für die Kopfzeile
  const worst = rows.reduce((acc, l) => {
    const m = FIT_STATUS[l.status];
    const w = m ? m.weight : 0;
    return w > acc.weight ? { weight: w, status: l.status } : acc;
  }, { weight: -99, status: null });
  const worstMeta = worst.status ? FIT_STATUS[worst.status] : null;

  // Kurzfassung fuer die zugeklappte Kopfzeile: Man soll ohne Aufklappen
  // sehen, ob etwas auffaellig ist. "Unter der Referenz" kommt aus zwei
  // Quellen — dem Status der Zeile und der belowReference-Liste des
  // Renderers — und wird ueber den Ebenennamen zusammengefuehrt.
  const belegt = rows.filter((l) => l.status !== "skipped");
  const belowNames = new Set(
    (Array.isArray(belowLayers) ? belowLayers : []).map(safeText),
  );
  rows.forEach((l) => {
    if (l.status === "fit_shrunk_below_reference") belowNames.add(safeText(l.layer));
  });
  const belowCount = belowNames.size;
  const manualCount = rows.filter((l) => l.status === "needs_manual").length;

  return (
    <details style={{ marginTop: "0.6em" }}>
      <summary style={{
        fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em",
        color: C.textLight, cursor: "pointer", userSelect: "none",
        display: "flex", alignItems: "center", gap: "0.5em", flexWrap: "wrap",
        listStyle: "none",
      }}>
        <span>▸ Fit-Report</span>
        <span style={{ textTransform: "none", letterSpacing: 0 }}>
          · {belegt.length} {belegt.length === 1 ? "Ebene" : "Ebenen"}
        </span>
        {belowCount > 0 && (
          <span style={{
            textTransform: "none", letterSpacing: 0, fontWeight: 700, color: C.amber,
          }}>· {belowCount} unter der Referenz</span>
        )}
        {manualCount > 0 && (
          <span style={{
            textTransform: "none", letterSpacing: 0, fontWeight: 700, color: C.red,
          }}>· {manualCount} manuell nötig</span>
        )}
        {worstMeta && worst.weight > 1 && belowCount === 0 && manualCount === 0 && (
          <span style={{
            textTransform: "none", letterSpacing: 0, fontWeight: 700,
            color: worstMeta.color,
          }}>· {worstMeta.label}</span>
        )}
        {styleSwitch && (
          <span style={{
            textTransform: "none", letterSpacing: 0, color: "#7b341e",
          }}>· Schrift gewechselt</span>
        )}
      </summary>
      <table style={{
        fontSize: "0.75rem", borderCollapse: "collapse", width: "100%",
        marginTop: "0.35em",
      }}>
        <thead>
          <tr style={{ color: C.textLight }}>
            <th style={{ textAlign: "left",  fontWeight: 500, padding: "1px 8px 1px 0" }}>Layer</th>
            <th style={{ textAlign: "left",  fontWeight: 500, padding: "1px 8px 1px 0" }}>Status</th>
            <th style={{ textAlign: "right", fontWeight: 500, padding: "1px 8px 1px 0" }}>Font px</th>
            <th style={{ textAlign: "right", fontWeight: 500, padding: "1px 8px 1px 0" }}>Zeilen</th>
            <th style={{ textAlign: "right", fontWeight: 500, padding: "1px 0" }}>Skalierung</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l, i) => {
            const meta = FIT_STATUS[l.status];
            const dim = l.status === "skipped";
            return (
              <tr key={i} style={{ color: dim ? C.textLight : C.textStrong }}>
                <td style={{ padding: "1px 8px 1px 0" }}>
                  {safeText(l.layer) || `#${i + 1}`}
                </td>
                <td style={{
                  padding: "1px 8px 1px 0", fontWeight: 600,
                  color: meta ? meta.color : C.textMuted,
                }} title={safeText(l.status)}>
                  {meta ? meta.label : (safeText(l.status) || "—")}
                </td>
                <td style={{ padding: "1px 8px 1px 0", textAlign: "right" }}>
                  {l.fontPx ?? "—"}
                </td>
                <td style={{ padding: "1px 8px 1px 0", textAlign: "right" }}>
                  {l.lines ?? "—"}
                </td>
                <td style={{
                  padding: "1px 0", textAlign: "right",
                  color: (Number(l.shrinkFactor) < 1) ? C.amber : C.textMuted,
                }}>
                  {fmtShrink(l.shrinkFactor)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* styleSwitch ist rein informativ — der Renderer musste den Spruch
          auf die Alternativschrift umstellen. */}
      {styleSwitch && (
        <div style={{
          marginTop: "0.4em", fontSize: "0.76rem", color: "#7b341e",
          background: C.amberSoft, border: `1px solid ${C.amber}`,
          borderRadius: 4, padding: "0.3em 0.55em", display: "inline-block",
        }}>{styleSwitch}</div>
      )}
    </details>
  );
};

/**
 * Alles, was der Kunde selbst geschrieben hat — an drei Stellen, jede
 * beschriftet. Nur sichtbar, wenn etwas da ist.
 *
 *   Nachricht zur Bestellung        etsy_orders.noteFromBuyer  (Etsy-Import)
 *   Kommentar bei „Auftrag bestätigen"  etsy_orders.orderFeedback  (submitOrderReview)
 *   Kommentar bei „Entwurf bestätigen"  drafts/*.customerFeedback  (je Entwurf)
 *
 * Warum das auf die Karte gehoert: Genau diese Texte sind der Grund, warum der
 * Auftragsdienst einen Fall an einen Menschen abgibt. Ein Aenderungswunsch zum
 * Entwurf ist der wichtigste — der Kandidat, der danach entsteht, soll genau
 * das umsetzen, also muss die Pruefende ihn sehen, ohne zu suchen.
 *
 * `drafts` sind ALLE Entwuerfe der Bestellung (Abfrage ueber orderNumber),
 * nicht nur die des Kandidaten — auch ein manueller Entwurf der Grafikerin
 * kann einen Kundenkommentar tragen, der fuer den naechsten Auto-Entwurf
 * zaehlt.
 */
const CustomerVoice = ({ order, drafts }) => {
  const note = safeText(order?.noteFromBuyer).trim();
  const orderFeedback = safeText(order?.orderFeedback).trim();
  const orderDecision = safeText(order?.orderDecision);

  const draftRows = (Array.isArray(drafts) ? drafts : [])
      .filter((d) => d && safeText(d.customerFeedback).trim())
      .sort((a, b) => tsMillis(a.createdAt) - tsMillis(b.createdAt));

  if (!note && !orderFeedback && draftRows.length === 0) return null;

  const Row = ({ label, meta, text, warn }) => (
    <div style={{ marginTop: "0.35em" }}>
      <div style={{
        fontSize: "0.7rem", color: warn ? "#742a2a" : C.textMuted,
        fontWeight: 600,
      }}>
        {label}{meta && <span style={{ fontWeight: 400 }}> · {meta}</span>}
      </div>
      <div style={{
        whiteSpace: "pre-wrap", fontSize: "0.83rem", color: C.textStrong,
        lineHeight: 1.45,
      }}>{text}</div>
    </div>
  );

  const decisionLabel = (d) =>
    d === "modification" ? "Änderungswunsch" :
    d === "approved" ? "freigegeben" :
    d === "confirmed" ? "bestätigt" : "";

  return (
    <div style={{
      marginTop: "0.6em", padding: "0.55em 0.8em", borderRadius: 6,
      background: "#ebf8ff", border: "1px solid #90cdf4",
    }}>
      <div style={{
        fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em",
        color: "#2a4365", fontWeight: 700,
      }}>Kunde schreibt</div>

      {note && (
        <Row label="Nachricht zur Bestellung" text={note} />
      )}
      {orderFeedback && (
        <Row label="Kommentar bei „Auftrag bestätigen“"
          meta={decisionLabel(orderDecision)}
          warn={orderDecision === "modification"}
          text={orderFeedback} />
      )}
      {draftRows.map((d, i) => (
        <Row key={d.id || i}
          label="Kommentar bei „Entwurf bestätigen“"
          meta={[
            draftRows.length > 1 ? `Entwurf ${i + 1} von ${draftRows.length}` : "",
            fmtTs(d.reviewedAt || d.createdAt),
            decisionLabel(d.customerDecision),
          ].filter(Boolean).join(" · ")}
          warn={d.customerDecision === "modification"}
          text={safeText(d.customerFeedback).trim()} />
      ))}
    </div>
  );
};

/**
 * Der Artikel, um den es geht — aus der Bestellzeile, nicht vom Kandidaten.
 * Der Kandidat kennt nur transactionId/listingId; der Titel steht an
 * etsy_orders.items[] (nach dem Scraper-Lauf der deutsche Seller-Hub-Titel).
 * Zuordnung ueber transactionId, ersatzweise listingId, zuletzt die erste
 * Position — eine Bestellung mit nur einer Position ist der Regelfall.
 */
export const productTitleFor = (order, cand) => {
  const items = Array.isArray(order?.items) ? order.items : [];
  if (!items.length) return "";
  const byTx = cand?.transactionId &&
    items.find((it) => String(it.transactionId || "") === String(cand.transactionId));
  const byListing = !byTx && cand?.listingId &&
    items.find((it) => String(it.listingId || "") === String(cand.listingId));
  const hit = byTx || byListing || (items.length === 1 ? items[0] : null);
  return safeText(hit?.title).trim();
};

const ProductLine = ({ order, cand }) => {
  const title = productTitleFor(order, cand);
  if (!title) return null;
  return (
    <div style={{
      fontSize: "0.82rem", color: C.textStrong, marginTop: "0.3em",
      lineHeight: 1.35,
    }} title="Artikel laut Bestellung">{title}</div>
  );
};

/**
 * Akzentwoerter (Personalization Tool, 13.09.2026): Ein Wort, das die
 * Pruefende im Schnellfix hervorgehoben hat, steht im Wert als `~Wort~` —
 * genau so wurde es gerendert. Hier wird es als Hervorhebung gezeigt statt
 * mit Tilden; die Kundin sieht ohnehin nur das Bild. Ein einzelnes `~`
 * ohne Gegenstueck bleibt Text.
 */
export const accentSegments = (text) => {
  const t = safeText(text);
  const out = [];
  const re = /~([^~\n]+)~/g;
  let last = 0, m;
  while ((m = re.exec(t)) !== null) {
    if (m.index > last) out.push({ text: t.slice(last, m.index), accent: false });
    out.push({ text: m[1], accent: true });
    last = m.index + m[0].length;
  }
  if (last < t.length) out.push({ text: t.slice(last), accent: false });
  return out;
};

const AccentedText = ({ text }) => (
  <>
    {accentSegments(text).map((seg, i) => seg.accent ? (
      <mark key={i} title="Im Schnellfix hervorgehoben (Akzentschrift)" style={{
        background: C.amberSoft, color: "#7b341e", fontWeight: 700,
        padding: "0 0.15em", borderRadius: 3,
      }}>{seg.text}</mark>
    ) : (
      <React.Fragment key={i}>{seg.text}</React.Fragment>
    ))}
  </>
);

/** Gemappte Werte in fester Reihenfolge; Newlines bleiben erhalten. */
const ValuesBlock = ({ values }) => {
  if (!values || typeof values !== "object") return null;
  const nonEmpty = (k) => {
    const t = safeText(values[k]);
    return t.trim() !== "";
  };
  const keys = VALUE_ORDER.filter((k) => k in values && nonEmpty(k));
  const extra = Object.keys(values).filter(
    (k) => !VALUE_ORDER.includes(k) && nonEmpty(k));
  if (keys.length === 0 && extra.length === 0) return null;

  return (
    <div style={{
      background: "#fdf7f4", borderLeft: `3px solid ${C.primaryDark}`,
      padding: "0.5em 0.75em", fontSize: "0.8rem", marginTop: "0.6em",
    }}>
      {[...keys, ...extra].map((k) => (
        <div key={k} style={{ marginBottom: "0.35em" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#5a4a44" }}>
            {VALUE_LABELS[k] || k}
          </div>
          <div style={{ whiteSpace: "pre-wrap", color: C.textStrong }}>
            <AccentedText text={values[k]} />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Kundenentscheidung zum bereits versendeten Entwurf.
 * Der Draft liegt unter drafts/{candidateId} — dieselbe ID wie der Kandidat.
 * Die Druckdatei wird erst freigegeben wenn der Kunde zugestimmt hat.
 */
const DraftOutcome = ({ cand, draft, draftLoading }) => {
  const box = (bg, border, color, children) => (
    <div style={{
      marginTop: "0.75em", padding: "0.5em 0.75em", borderRadius: 6,
      background: bg, border: `1px solid ${border}`, color,
      fontSize: "0.8rem",
    }}>{children}</div>
  );

  if (draftLoading) {
    return box(C.greySoft, C.border, C.textMuted, "Entwurfs-Status wird geladen…");
  }

  if (!draft) {
    return box(C.redSoft, "#fc8181", "#742a2a",
      <>
        <strong>Kein Entwurf gefunden</strong> — erwartet unter{" "}
        <code>drafts/{cand.id}</code>. Möglicherweise wurde er nachträglich
        gelöscht oder nach 28 Tagen automatisch aufgeräumt.
      </>);
  }

  // Manuell/automatisch archiviert, ohne Kundenreaktion
  if (draft.draftArchived && !draft.customerDecision) {
    return box(C.greySoft, C.border, C.textMuted,
      <><strong>Entwurf archiviert</strong> — der Kunde hat nicht reagiert,
        die Bestellung lief weiter.</>);
  }

  // Noch keine Reaktion
  if (!draft.reviewedAt || !draft.customerDecision) {
    return box(C.amberSoft, "#f6ad55", "#7b341e",
      <>
        <strong>Wartet auf Kundenfreigabe</strong>
        {draft.emailSentAt && (
          <span style={{ color: "#975a16" }}>
            {" "}· gesendet {fmtTs(draft.emailSentAt)}
          </span>
        )}
        {draft.emailSendError && (
          <div style={{ marginTop: "0.25em", color: "#742a2a" }}>
            ⚠ E-Mail-Versand meldete einen Fehler — bitte in Entwürfe prüfen.
          </div>
        )}
      </>);
  }

  // Änderungswunsch. Stammt der Entwurf aus dem Auto-Pfad (uploaderIp), lässt
  // sich der Wunsch direkt im lokalen Editor umsetzen; der korrigierte Stand
  // kommt als neuer Kandidat zurück und geht über die normale Freigabe erneut
  // an den Kunden.
  if (draft.customerDecision === "modification") {
    const fromAutoPipeline = draft.uploaderIp === "auto:draft_candidates";
    return box(C.redSoft, "#fc8181", "#742a2a",
      <>
        <strong>Änderungswunsch:</strong>{" "}
        <span style={{ whiteSpace: "pre-wrap" }}>
          {draft.customerFeedback || "— (kein Text angegeben)"}
        </span>
        <div style={{ marginTop: "0.25em", color: "#975a16", fontSize: "0.75rem" }}>
          {fmtTs(draft.reviewedAt)} · Druckdatei bleibt gesperrt
        </div>
        {fromAutoPipeline && (
          <div style={{
            marginTop: "0.5em", display: "flex", alignItems: "center",
            gap: "0.6em", flexWrap: "wrap",
          }}>
            <EditorLink candidateId={cand.id} compact />
            <span style={{ fontSize: "0.72rem", color: "#975a16" }}>
              Korrektur im Editor · der neue Kandidat geht über die normale
              Freigabe erneut an den Kunden
            </span>
          </div>
        )}
      </>);
  }

  // Vom Kunden freigegeben → Druckdatei
  return (
    <div style={{ marginTop: "0.75em" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "0.7em", flexWrap: "wrap",
      }}>
        <Badge bg={C.greenSoft} color="#22543d" border="#68d391">
          Vom Kunden freigegeben ✓
        </Badge>
        <span style={{ fontSize: "0.75rem", color: C.textMuted }}>
          {fmtTs(draft.reviewedAt)}
        </span>
      </div>
      {cand.printUrl ? (
        <a href={cand.printUrl} target="_blank" rel="noopener noreferrer"
           style={{
             display: "inline-block", marginTop: "0.5em",
             padding: "0.45em 1em", borderRadius: 6,
             background: C.primaryDark, color: "#fff", fontSize: "0.82rem",
             fontWeight: 600, textDecoration: "none",
           }}>⬇ Druckdatei (PNG)</a>
      ) : (
        <div style={{ marginTop: "0.4em", fontSize: "0.78rem", color: C.red }}>
          ⚠ Keine printUrl am Kandidaten hinterlegt.
        </div>
      )}
      {draft.customerFeedback && (
        <div style={{
          marginTop: "0.4em", fontSize: "0.78rem", color: C.textMuted,
          whiteSpace: "pre-wrap",
        }}>Kundenkommentar: {draft.customerFeedback}</div>
      )}

      {/* Drive-Ablage: der Scraper legt die Druckdatei nach der
          Kundenfreigabe im Ordner "aktuell" ab. */}
      {cand.driveUploadedAtMs > 0 ? (
        <div style={{
          marginTop: "0.5em", fontSize: "0.78rem", color: "#22543d",
        }}>
          In Drive abgelegt ✓
          {cand.driveFileName ? ` (${cand.driveFileName})` : ""}
        </div>
      ) : cand.driveUploadError ? (
        <div style={{ marginTop: "0.5em", fontSize: "0.78rem", color: C.red }}>
          ⚠ Drive-Ablage fehlgeschlagen: {cand.driveUploadError}
        </div>
      ) : (
        <div style={{ marginTop: "0.5em", fontSize: "0.78rem", color: C.textMuted }}>
          Drive-Ablage läuft — der Scraper legt die Druckdatei in "aktuell" ab.
        </div>
      )}
    </div>
  );
};


/**
 * Aufklappbare Liste der ersetzten/abgelehnten Vorgängerversionen eines
 * Kandidaten. Jede Zeile lässt sich einzeln entfernen — das Firestore-
 * Dokument. Die Bilder unter `candidates/` bleiben liegen und werden vom
 * Personalization Tool aufgeräumt (seit 14.09.2026; siehe die Begründung
 * an `handleDeleteCandidate`).
 *
 * Das Löschen ist bewusst manuell: es gibt derzeit keine automatische
 * Aufbewahrungsregel für Kandidaten (siehe docs/housekeeping.md).
 */
const VersionHistory = ({ history }) => {
  const [open, setOpen] = useState(false);

  if (!history || history.length === 0) return null;

  return (
    <div style={{ marginTop: "0.75em", borderTop: `1px solid ${C.border}`, paddingTop: "0.6em" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: "none", border: "none", padding: 0, cursor: "pointer",
          fontSize: "0.8rem", color: C.textMuted, fontWeight: 600,
        }}
      >
        {open ? "▾" : "▸"} {history.length}{" "}
        {history.length === 1 ? "frühere Version" : "frühere Versionen"}
      </button>

      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.35em", marginTop: "0.5em" }}>
          {history.map((h) => {
            const meta = STATUS_BADGE[h.status] || STATUS_BADGE.superseded;
            return (
              <div key={h.id} style={{
                display: "flex", alignItems: "center", gap: "0.6em", flexWrap: "wrap",
                fontSize: "0.78rem", background: C.greySoft,
                border: `1px solid ${C.border}`, borderRadius: 6,
                padding: "0.4em 0.6em",
              }}>
                <Badge {...meta}>{meta.label}</Badge>
                <span style={{ color: C.textMuted }}>{fmtTs(h.createdAt)}</span>
                {h.templateName && (
                  <span style={{ color: C.textLight }}>{h.templateName}</span>
                )}
                {h.status === "rejected" && h.reviewNote && (
                  <span style={{ color: "#742a2a" }}>· {h.reviewNote}</span>
                )}

                <span style={{ marginLeft: "auto", display: "flex", gap: "0.7em", alignItems: "center" }}>
                  {h.previewUrl && (
                    <a href={h.previewUrl} target="_blank" rel="noopener noreferrer"
                       style={{ color: C.primaryDark }}>Vorschau ↗</a>
                  )}
                  {h.printUrl && (
                    <a href={h.printUrl} target="_blank" rel="noopener noreferrer"
                       style={{ color: C.primaryDark }}>Druckdatei ↗</a>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ── Lokaler Editor des Personalization Tools ──────────────────────────────
//
// Laeuft als uvicorn-Dienst auf demselben PC wie der Renderer. Der Link
// funktioniert deshalb nur dort — auf jedem anderen Rechner laeuft er ins
// Leere. Das steht so im Tooltip, damit niemand einen Fehler vermutet.
//
// Stand 2026-09-07 nimmt /editor ausschliesslich template_id entgegen und
// antwortet auf ?candidate=... mit HTTP 422. Der Parametername hier ist so
// vorgegeben; sobald der Editor den Kandidaten kennt, passt es zusammen.
// Andernfalls hier auf template_id umstellen.

const EDITOR_BASE = "http://localhost:8010/editor";
const EDITOR_HINT =
  "Oeffnet den lokalen Editor des Personalization Tools (localhost:8010). " +
  "Funktioniert nur auf dem PC, auf dem das Tool laeuft.";

/**
 * Wann die Karte den Schnellfix anbietet: bei jedem `pending_review`, und
 * bei `needs_manual` nur, wenn ein Bild da ist — das ist der Fall
 * „Akzentwoerter" (13.09.2026): der Auftragsdienst hat gerendert und
 * hochgeladen, aber die Wahl der hervorzuhebenden Woerter einem Menschen
 * ueberlassen. Ein `needs_manual` ohne Bild hat nichts zu bearbeiten.
 */
export const canOpenEditor = (cand) =>
  !!cand && (cand.status === "pending_review" ||
    (cand.status === "needs_manual" && !!cand.previewUrl));

const EditorLink = ({ candidateId, compact = false }) => (
  <a
    href={`${EDITOR_BASE}?candidate=${encodeURIComponent(candidateId)}`}
    target="_blank"
    rel="noopener noreferrer"
    title={EDITOR_HINT}
    style={{
      display: "inline-block",
      padding: compact ? "0.25em 0.7em" : "0.5em 1.1em",
      borderRadius: 6,
      border: `1px solid ${C.border}`,
      background: "#fff",
      color: C.textStrong,
      fontSize: compact ? "0.76rem" : "0.85rem",
      textDecoration: "none",
      whiteSpace: "nowrap",
    }}
  >Bearbeiten (lokal) ↗</a>
);

/**
 * Korrekturen, die im lokalen Editor vorgenommen wurden, als eine kompakte
 * Zeile: "Umbruch, Groesse spruch 93→85".
 *
 * Der Contract steht noch nicht fest, deshalb werden beide plausiblen Formen
 * gelesen — eine Liste von Eintraegen und eine Zuordnung Feld → Wert:
 *   [{label:"Umbruch"}, {label:"Groesse", layer:"spruch", from:93, to:85}]
 *   {Umbruch: true, "Groesse spruch": [93, 85]}
 * Was sich nicht deuten laesst, wird stillschweigend ausgelassen.
 */
const OVERRIDE_KIND_LABELS = {
  text:         "Text",
  linebreak:    "Umbruch",
  size:         "Größe",
  style:        "Stil",
  anlass:       "Anlass",
  position:     "Position",
  line_spacing: "Zeilenabstand",
  faux_bold:    "Fettdruck",
  caps:         "Großbuchstaben",
  accent:       "Hervorgehoben",
  motif:        "Motiv",
  accent_ratio: "Akzentwörter",
  accent_font:  "Akzentschrift",
  color:        "Schriftfarbe",
  // 2026-09-27, Notiz des Tools: target_chars ist neu, die fuenf darunter
  // schickt es seit Stufe drei. Sie sind nie verschwunden — kindLabel gibt ein
  // unbekanntes Token unveraendert zurueck, in der Zeile stand also
  // "zone spruch 132,2020 1152×778→…". Sichtbar, aber auf Englisch und in
  // Maschinensprache; die Wortwahl stammt aus dem Schnellfix-Editor, damit
  // beide Oberflaechen dieselbe Sache gleich nennen.
  target_chars:   "Zeichen je Zeile",
  font:           "Schrift",
  letter_spacing: "Laufweite",
  stroke:         "Strich",
  zone:           "Zone",
  note_position:  "Hinweislage",
};

/**
 * Werte, die fuer die Anzeige aufbereitet werden. Nur dort, wo der Rohwert
 * die Zeile unleserlich macht: eine Schriftdatei ist ein Dateiname, kein
 * Name — "Martin kamila.ttf→CARROISGOTHICSC-REGULAR.TTF" schreit, und die
 * Endung traegt nichts bei. Alles andere bleibt roh: bei Zone und
 * Hinweislage SIND die Koordinaten die Aussage.
 */
const OVERRIDE_VALUE_FORMAT = {
  font:        (v) => prettyFontName(v),
  accent_font: (v) => prettyFontName(v),
};

/**
 * Farbwerte in der Korrigiert-Zeile bekommen ein Kaestchen in genau der
 * Farbe. Die Art `color` (Notiz des Tools vom 17.09.2026) aendert die
 * Schriftfarbe einer Ebene fuer EINE Bestellung; die Zeile liest sich dann
 * "Schriftfarbe name #35503c→#40513f". Zwei Gruentoene sind als Zahl nicht
 * zu unterscheiden — genau das muss die Pruefende aber sehen, wenn sie die
 * Kerze mit der Vorlage vergleicht.
 *
 * Reine Textaufteilung, damit overridesLine eine Zeichenkette bleibt: was
 * nach einem Farbcode aussieht (Gatter, sechs Hex-Ziffern), wird zu einem
 * eigenen Abschnitt mit `color`, alles andere bleibt Text.
 */
export const colorSegments = (text) => {
  const src = safeText(text);
  const re = new RegExp("#[0-9a-fA-F]{6}", "g");
  const out = [];
  let last = 0;
  let m = re.exec(src);
  while (m !== null) {
    if (m.index > last) out.push({ text: src.slice(last, m.index) });
    out.push({ text: m[0], color: m[0].toLowerCase() });
    last = m.index + m[0].length;
    m = re.exec(src);
  }
  if (last < src.length) out.push({ text: src.slice(last) });
  return out;
};

/**
 * Schriftdatei -> lesbarer Name: "MARIGOLD DREAMER.TTF" -> "Marigold Dreamer".
 * Die Endung faellt weg. Umgeschrieben wird NUR, wenn der Name durchgehend
 * gross steht — sonst wuerde aus "DejaVuSans-Bold" ein "Dejavusans-bold",
 * und die Eigenschreibweise einer Schrift ist ein Name, kein Zufall.
 */
const prettyFontName = (raw) => {
  const base = safeText(raw).trim().replace(/\.(ttf|otf|woff2?|ttc)$/i, "");
  if (!base) return "";
  if (/[a-zäöüß]/.test(base)) return base;          // enthaelt Kleinbuchstaben
  return base.toLowerCase().replace(/(^|[\s\-_])(\p{L})/gu,
    (_, sep, ch) => sep + ch.toUpperCase());
};

/**
 * Die drei Arten aus der Notiz vom 14.09.2026. Sie tragen ihre Aussage im
 * `to`-Wert; Ebene und Vorher-Wert helfen der Pruefenden nicht weiter
 * (`motiv`, `0`, eine leere Vorlagenschrift). Roh gerendert stand dort
 * "accent_font spruch →MARIGOLD DREAMER.TTF" — mit haengendem Pfeil.
 * Ein unverstaendlicher Wert faellt auf die allgemeine Darstellung zurueck.
 */
const specialOverrideText = (kind, o) => {
  const to = safeText(o.to ?? o.new ?? o.after ?? o.nachher ?? "").trim();
  if (kind === "motif") {
    // Senkrechter Versatz der Hintergrundgrafik in Pixeln des Druckbilds,
    // gemessen ab der Vorlage. Negativ = nach oben. `from` ist immer 0 und
    // meint die Vorlage — auch bei einer zweiten Korrektur an derselben
    // Kerze steht dort 0, nicht der uebernommene Versatz (bestaetigt
    // 14.09.2026). Deshalb traegt allein `to` die Aussage.
    const px = Number(to);
    if (!Number.isFinite(px)) return "";
    // Das Tool schreibt bei 0 GAR KEINEN Eintrag — steht das Motiv wie in
    // der Vorlage, meldet es nichts. Der Zweig ist also Vorsorge und bleibt
    // trotzdem: ohne ihn stuende hier "Motiv 0 px abgesenkt", und das waere
    // schlechter als eine Zeile, die nie erscheint.
    if (px === 0) return "Motiv wieder wie in der Vorlage";
    return `Motiv ${Math.abs(px)} px ${px < 0 ? "angehoben" : "abgesenkt"}`;
  }
  if (kind === "accent_ratio") {
    // Anteil an der Schriftgroesse der Zeile: 1.2 = 120 %.
    const r = Number(to);
    if (!Number.isFinite(r)) return "";
    return `Akzentwörter ${Math.round(r * 100)} %`;
  }
  if (kind === "accent_font") {
    if (!to || to.toLowerCase() === "keine") return "Akzentschrift entfernt";
    return `Akzentschrift: ${prettyFontName(to)}`;
  }
  return "";
};

export const overridesLine = (raw) => {
  const parts = [];
  // Lange Werte wuerden die kompakte Zeile sprengen — z. B. ein kompletter
  // Spruch bei kind "text".
  const val = (v) => {
    // Ein Umbruch IST bei kind "linebreak" die Aenderung. Wuerde er zu einem
    // Leerzeichen zusammenfallen, staende dort "Elli Hedwig→Elli Hedwig" —
    // beide Seiten gleich, die Korrektur unsichtbar.
    const t = safeText(v).replace(/\r/g, "").replace(/\n/g, "\u23ce")
      .replace(/[ \t]+/g, " ").trim();
    return t.length > 40 ? t.slice(0, 39) + "…" : t;
  };
  // kind kommt als Maschinen-Token (text|linebreak|size|style|anlass) und
  // wird fuer die Anzeige uebersetzt; unbekannte Werte bleiben unveraendert.
  const kindLabel = (v) => {
    const t = safeText(v).trim();
    return OVERRIDE_KIND_LABELS[t.toLowerCase()] || t;
  };

  const fromTo = (o, fmt) => {
    const f = o.from ?? o.old ?? o.before ?? o.vorher;
    const t = o.to ?? o.new ?? o.after ?? o.nachher;
    if (f === undefined || f === null || t === undefined || t === null) return "";
    // Der Aufbereiter darf nie zu einer leeren Seite fuehren: lieber der
    // Rohwert als ein haengender Pfeil.
    const one = (v) => {
      const pretty = fmt ? safeText(fmt(v)).trim() : "";
      return pretty || val(v);
    };
    return `${one(f)}→${one(t)}`;
  };

  if (Array.isArray(raw)) {
    for (const o of raw) {
      if (typeof o === "string" || typeof o === "number") {
        if (val(o)) parts.push(val(o));
        continue;
      }
      if (!o || typeof o !== "object") continue;
      const kindRaw = safeText(o.kind ?? o.label ?? o.type ?? o.name ?? "").trim();
      // "carried" ist keine Korrektur DIESES Durchgangs, sondern eine aus
      // dem Vorgaenger uebernommene Einstellung. Sie bekommt eine eigene
      // Zeile (carriedLine) — unter "Korrigiert:" gelesen wuerde sie der
      // Pruefenden eine Handarbeit zuschreiben, die sie nie gemacht hat.
      if (kindRaw.toLowerCase() === "carried") continue;
      // "accent": Ebene und Vorher-Wert ("—") tragen nichts — es zaehlt,
      // welche Woerter jetzt in der Akzentschrift stehen.
      if (kindRaw.toLowerCase() === "accent") {
        const words = val(o.to ?? o.new ?? o.after ?? o.nachher ?? "");
        parts.push(words ? `Hervorgehoben: ${words}` : "Hervorgehoben");
        continue;
      }
      const special = specialOverrideText(kindRaw.toLowerCase(), o);
      if (special) { parts.push(special); continue; }
      const label = kindLabel(kindRaw);
      const layer = val(o.layer ?? o.field ?? o.target ?? "");
      const change = fromTo(o, OVERRIDE_VALUE_FORMAT[kindRaw.toLowerCase()]);
      const line = [label, layer, change].filter(Boolean).join(" ");
      if (line) parts.push(line);
    }
  } else if (raw && typeof raw === "object") {
    for (const [k, v] of Object.entries(raw)) {
      if (k === "advanced") continue;   // Schalter, keine Korrektur
      if (v === false || v === null || v === undefined || v === "") continue;
      if (v === true) { parts.push(kindLabel(k)); continue; }
      if (Array.isArray(v)) {
        if (v.length === 2) parts.push(`${kindLabel(k)} ${val(v[0])}→${val(v[1])}`);
        else if (v.length) parts.push(`${kindLabel(k)} ${v.map(val).join("/")}`);
        continue;
      }
      if (typeof v === "object") {
        const change = fromTo(v);
        parts.push(change ? `${kindLabel(k)} ${change}` : `${kindLabel(k)} ${val(v)}`);
        continue;
      }
      parts.push(`${kindLabel(k)} ${val(v)}`);
    }
  }
  return parts.filter(Boolean).join(", ");
};

/**
 * „Uebernommen aus dem vorigen Entwurf: Groesse, Zeilenabstand, …"
 *
 * Das Personalization Tool setzt seit 14.09.2026 einen Eintrag
 * {kind:"carried", layer:"-", from:"Vorlage", to:"aus dem vorigen Entwurf
 * uebernommen: …"}, wenn ein Entwurf die Regler seines Vorgaengers erbt.
 * Das Tool sichert zu (14.09.2026): **genau ein** solcher Eintrag je
 * Kandidat oder keiner, immer mit layer "-" (uebernommen wird das ganze
 * Buendel oder nichts), und `carried` steht nie zusammen mit echten
 * Korrekturen — entweder der Auftragsdienst hat uebernommen, oder der
 * Schnellfix hat korrigiert. Mehrere Eintraege werden trotzdem verbunden,
 * falls sich das eines Tages aendert.
 * Roh durchgereicht stand dort "carried - Vorlage→aus dem vorigen Entwurf
 * uebernommen: Grö…" — die 40-Zeichen-Kuerzung schnitt genau die Aufzaehlung
 * ab, auf die es ankommt. Deshalb eigene Behandlung: Ebene ("-") und
 * Vorher-Wert ("Vorlage") tragen nichts, der doppelte Vorspann faellt weg,
 * und es bleibt mehr Platz fuer die Aufzaehlung selbst.
 */
export const carriedLine = (raw) => {
  if (!Array.isArray(raw)) return "";
  const out = [];
  for (const o of raw) {
    if (!o || typeof o !== "object") continue;
    const kind = safeText(o.kind ?? o.label ?? o.type ?? o.name ?? "").trim();
    if (kind.toLowerCase() !== "carried") continue;
    const what = safeText(o.to ?? o.new ?? o.after ?? o.nachher ?? "")
      .replace(/\s+/g, " ")
      // "aus dem vorigen Entwurf uebernommen: X" -> "X"; die Zeile sagt das
      // schon in ihrer Beschriftung.
      .replace(/^.*?übernommen\s*:\s*/i, "")
      .trim();
    // Grenze 120, nicht 90: Der laengste Fall des Tools ist
    // "Größe, Position, …, Großbuchstaben und 8 weitere" (71 Zeichen).
    // Greift der Vorspann-Abzug oben einmal nicht, weil das Tool die
    // Formulierung geaendert hat, steht der Vorspann (36 Zeichen) noch
    // davor — zusammen 107. Bei 90 fiele ausgerechnet das "und 8 weitere"
    // weg, also die Angabe, dass da noch mehr ist.
    out.push(what.length > 120 ? what.slice(0, 119) + "…" : what);
  }
  return out.filter(Boolean).join(", ");
};

/** Eigene, ruhige Zeile — keine Korrektur, sondern eine Herkunftsangabe. */
const CarriedLine = ({ text }) => {
  if (!text) return null;
  return (
    <div
      title={"Diese Einstellungen stammen aus der vorherigen Korrektur zu " +
             "dieser Bestellung, nicht aus diesem Durchgang."}
      style={{
        marginTop: "0.4em", fontSize: "0.74rem", color: C.textMuted,
        background: C.greySoft, border: `1px solid ${C.border}`,
        borderRadius: 4, padding: "0.25em 0.55em", display: "inline-block",
      }}
    >
      ↩ <strong>Übernommen</strong> aus dem vorigen Entwurf: {text}
    </div>
  );
};

/**
 * True, sobald mindestens ein Eintrag advanced: true traegt — dann wurde im
 * Editor ueber die einfachen Korrekturen hinaus eingegriffen.
 */
const overridesHasAdvanced = (raw) => {
  if (Array.isArray(raw)) {
    return raw.some((o) => o && typeof o === "object" && o.advanced === true);
  }
  if (raw && typeof raw === "object") return raw.advanced === true;
  return false;
};

/** Kompakte Zeile "Korrigiert: …" auf der Karte. */
const OverridesLine = ({ text, advanced }) => {
  if (!text) return null;
  return (
    <div style={{
      marginTop: "0.4em", fontSize: "0.76rem", color: "#2a4365",
      background: C.blueSoft, border: "1px solid #90cdf4",
      borderRadius: 4, padding: "0.25em 0.55em", display: "inline-block",
    }}>
      <strong>Korrigiert:</strong>{" "}
      {colorSegments(text).map((seg, i) => seg.color ? (
        <span key={i} style={{ whiteSpace: "nowrap" }}>
          <span aria-hidden="true" style={{
            display: "inline-block", width: "0.72em", height: "0.72em",
            background: seg.color, border: "1px solid #718096",
            borderRadius: 2, marginRight: "0.22em", verticalAlign: "-0.04em",
          }} />
          {seg.text}
        </span>
      ) : <span key={i}>{seg.text}</span>)}
      {advanced && (
        <span
          title="Im Editor wurde ueber die einfachen Korrekturen hinaus eingegriffen"
          style={{
            marginLeft: "0.5em", fontSize: "0.66rem", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.04em",
            padding: "0.05em 0.4em", borderRadius: 999,
            background: C.amberSoft, color: "#7b341e",
            border: `1px solid ${C.amber}`,
          }}
        >erweitert</span>
      )}
    </div>
  );
};

/**
 * Hinweis des Renderers an den Kunden. Wird bei der Freigabe in den Draft
 * uebernommen und erscheint dort auf der Review-Seite und in der ersten
 * E-Mail ({{HINWEIS}}) — hier steht er, damit Alexandra vor dem Senden
 * sieht, was der Kunde lesen wird.
 */
const CustomerNotice = ({ notice }) => {
  if (!notice) return null;
  return (
    <div style={{
      marginTop: "0.6em", padding: "0.6em 0.8em", borderRadius: 6,
      background: C.amberSoft, border: `1px solid ${C.amber}`,
      color: "#7b341e", fontSize: "0.83rem", lineHeight: 1.45,
    }}>
      <div style={{
        fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em",
        fontWeight: 700, marginBottom: "0.2em",
      }}>Hinweis an Kunden</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{notice}</div>
    </div>
  );
};


/**
 * Erklaert einen erzwungenen Entwurf: warum die Bestellung eigentlich
 * manuell war, was der Watcher stattdessen getan hat, und wer das
 * angestossen hat.
 *
 * Die Zeile enthaelt bei uebergangenen Kundennotizen deren Wortlaut — beim
 * Pruefen ist genau das die Information, die man sehen muss, bevor man
 * freigibt. Sie bleibt intern: der Freigabepfad schreibt eine feste
 * Feldmenge nach drafts/, forcedReason ist nicht darunter.
 */
const ForcedNotice = ({ cand }) => {
  if (!cand?.forced) return null;
  const placeholders = Array.isArray(cand.forcedPlaceholders)
    ? cand.forcedPlaceholders.filter((p) => p !== "unbenannt") : [];
  const hasPlaceholders = (cand.forcedPlaceholders || []).length > 0;
  return (
    <div style={{
      marginTop: "0.6em", padding: "0.6em 0.8em", borderRadius: 6,
      background: "#fffaf0", border: "1px solid #dd6b20",
      color: "#7b341e", fontSize: "0.83rem", lineHeight: 1.45,
    }}>
      <div style={{
        fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em",
        fontWeight: 700, marginBottom: "0.2em",
      }}>
        Erzwungener Entwurf — bitte genau prüfen
      </div>
      {cand.forcedReason && (
        <div style={{ whiteSpace: "pre-wrap" }}>{cand.forcedReason}</div>
      )}
      {hasPlaceholders && (
        <div style={{
          marginTop: "0.4em", fontWeight: 700, color: "#742a2a",
        }}>
          ✗ Enthält noch Platzhalter (xxxxx)
          {placeholders.length ? ` für: ${placeholders.join(", ")}` : ""} —
          Freigabe ist gesperrt, bis sie im Schnellfix ersetzt sind.
        </div>
      )}
      {cand.forcedBy && (
        <div style={{
          marginTop: "0.3em", fontSize: "0.72rem", color: C.textMuted,
        }}>
          erzwungen von {cand.forcedBy}
        </div>
      )}
    </div>
  );
};


/** Pfeil-Schaltflaeche des Referenz-Karussells. */
// Beide Vergleichsbilder bekommen exakt dieselbe Anzeigebox: gleiche Breite,
// gleiche Hoehe, contain. Nur so erscheinen Kranz und Text links wie rechts
// in derselben Groesse — die Spalten sind zwar gleich breit, aber ohne feste
// Box wuerde jedes Bild seine eigene Hoehe bestimmen.
const COMPARE_IMG_BOX = {
  width: "100%",
  maxWidth: 340,
  height: 440,
  objectFit: "contain",
  display: "block",
  background: "#fff",
  border: `1px solid ${C.border}`,
  borderRadius: 8,
};

const navBtn = (side) => ({
  position: "absolute", top: "50%", transform: "translateY(-50%)",
  [side]: 6,
  width: 30, height: 44, borderRadius: 6, cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.7)",
  background: "rgba(45,55,72,0.55)", color: "#fff",
  fontSize: "1.4rem", lineHeight: 1, padding: 0,
});

/**
 * Prüfansicht: Auto-Entwurf gegen die Referenzdrucke der Grafikerin.
 *
 * Drei Spalten — links die Vorschau (Klick vergrößert), Mitte die gemappten
 * Werte plus Fit-Report, rechts die Referenzbilder als Karussell.
 *
 * WICHTIG: Die Referenzbilder zeigen freigegebene Arbeiten ANDERER Kunden.
 * Sie sind ausschliesslich für diese interne Ansicht bestimmt. Sie werden
 * nirgends in den Kundenpfad übernommen: handleApprove schreibt einen
 * festen Feldsatz nach drafts/{id}, und die Firestore-Regel pinnt genau
 * diese Felder — referenceThumbs kann dort nicht landen.
 */
const CompareView = ({ cand, order, orderLoading, onApprove, onReject,
  busyId, toast, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(null);   // null | "draft" | number (Referenz)
  const [noteDraft, setNoteDraft] = useState("");
  const [showReject, setShowReject] = useState(false);

  const thumbs = cand.referenceThumbs || [];
  const { canSend, disabledHint } = computeGating(cand, order, orderLoading);
  const busy = busyId === cand.id;

  // Sobald der Kandidat die Prüfung verlässt (freigegeben/abgelehnt/ersetzt),
  // hat die Ansicht keinen Zweck mehr.
  useEffect(() => {
    if (cand.status !== "pending_review") onClose();
  }, [cand.status, onClose]);

  // Tastatur: Pfeile blättern, Escape schliesst. In Eingabefeldern nicht.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA"
        || t.isContentEditable);
      if (e.key === "Escape") {
        if (zoom !== null) { setZoom(null); return; }
        if (!typing) onClose();
        return;
      }
      if (typing) return;
      if (e.key === "ArrowLeft" && thumbs.length > 1) {
        e.preventDefault();
        setIdx((i) => (i - 1 + thumbs.length) % thumbs.length);
      }
      if (e.key === "ArrowRight" && thumbs.length > 1) {
        e.preventDefault();
        setIdx((i) => (i + 1) % thumbs.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [thumbs.length, onClose, zoom]);

  const current = thumbs[idx] || null;

  // Feste Hoehe und eine Zeile: bricht eine Ueberschrift um, saehe das
  // zugehoerige Bild tiefer aus als das daneben.
  const colHead = (t) => (
    <div style={{
      fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em",
      color: C.textLight, fontWeight: 700, marginBottom: "0.5em",
      height: "1.2em", lineHeight: "1.2em",
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    }}>{t}</div>
  );

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(26,32,44,0.55)",
        display: "flex", flexDirection: "column",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#fdf7f4", flex: 1, margin: "1.5em",
        borderRadius: 12, display: "flex", flexDirection: "column",
        overflow: "hidden", boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
      }}>
        {/* Kopfzeile */}
        <div style={{
          display: "flex", alignItems: "center", gap: "0.8em", flexWrap: "wrap",
          padding: "0.9em 1.2em", borderBottom: `1px solid ${C.border}`,
          background: C.cardBg,
        }}>
          <strong style={{ color: C.textStrong, fontSize: "1.05rem" }}>
            Prüfansicht #{cand.orderId}
          </strong>
          <Badge {...STATUS_BADGE.pending_review}>Prüfung offen</Badge>
          <span style={{ fontSize: "0.8rem", color: C.textMuted }}>
            {cand.templateName || cand.templateId || "—"}
            {order?.buyerName && <> · {order.buyerName}</>}
          </span>
          <span style={{
            marginLeft: "auto", fontSize: "0.72rem", color: C.textLight,
          }}>
            ← → blättern · Esc schliesst
          </span>
          <button
            onClick={onClose}
            style={{
              border: `1px solid ${C.border}`, background: "#fff",
              borderRadius: 6, padding: "0.35em 0.9em", cursor: "pointer",
              color: C.textStrong, fontSize: "0.85rem",
            }}
          >Schliessen</button>
        </div>

        {toast && (
          <div style={{
            padding: "0.6em 1.2em", fontSize: "0.85rem",
            background: toast.type === "ok" ? C.greenSoft : C.redSoft,
            color: toast.type === "ok" ? "#22543d" : "#742a2a",
            borderBottom: `1px solid ${toast.type === "ok" ? "#68d391" : "#fc8181"}`,
          }}>{toast.msg}</div>
        )}

        {/* Drei Spalten */}
        <div style={{
          flex: 1, overflow: "auto", padding: "1.2em",
          display: "grid", gap: "1.2em",
          // Alle drei gleich breit: die aeussere Breite geht sonst in die
          // Bildgroesse ein und die Bilder waeren wieder verschieden gross.
          gridTemplateColumns: "repeat(3, minmax(260px, 1fr))",
          alignItems: "start",
        }}>
          {/* Links: Auto-Entwurf */}
          <div>
            {colHead("Auto-Entwurf")}
            {cand.previewUrl ? (
              <img
                src={cand.previewUrl}
                alt="Entwurfsvorschau"
                onClick={() => setZoom("draft")}
                style={{ ...COMPARE_IMG_BOX, cursor: "zoom-in" }}
              />
            ) : (
              <div style={{
                width: "100%", maxWidth: COMPARE_IMG_BOX.maxWidth,
                height: COMPARE_IMG_BOX.height,
                display: "flex", alignItems: "center", justifyContent: "center",
                textAlign: "center", borderRadius: 8, padding: "0 1em",
                boxSizing: "border-box",
                background: C.greySoft, border: `1px dashed ${C.border}`,
                color: C.textLight, fontSize: "0.85rem",
              }}>keine Vorschau</div>
            )}
            <ForcedNotice cand={cand} />
            <CustomerNotice notice={cand.customerNotice} />
            {cand.printUrl && (
              <a href={cand.printUrl} target="_blank" rel="noopener noreferrer"
                 style={{
                   display: "inline-block", marginTop: "0.6em",
                   fontSize: "0.8rem", color: C.primaryDark,
                 }}>Druckdatei ↗</a>
            )}
          </div>

          {/* Mitte: Werte + Fit-Report */}
          <div>
            {colHead("Personalisierung")}
            <OverridesLine text={cand.overridesText}
              advanced={cand.overridesAdvanced} />
            <CarriedLine text={cand.carriedText} />
            <ValuesBlock values={cand.valuesObj} />
            <FitReport fitReport={cand.fitReportList}
              styleSwitch={cand.styleSwitchText}
              belowLayers={cand.belowReferenceLayers} />
            {cand.reasonList?.length > 0 && (
              <div style={{
                marginTop: "0.6em", fontSize: "0.78rem", color: "#7b341e",
                background: C.amberSoft, border: `1px solid ${C.amber}`,
                padding: "0.4em 0.6em", borderRadius: 4,
              }}>
                <strong>Hinweise:</strong> {cand.reasonList.join(" · ")}
              </div>
            )}
          </div>

          {/* Rechts: Referenzen */}
          <div>
            {colHead(
              thumbs.length > 0
                ? `Referenzen der Grafikerin (${idx + 1}/${thumbs.length})`
                : "Referenzen der Grafikerin")}

            {thumbs.length === 0 ? (
              <div style={{
                width: "100%", maxWidth: COMPARE_IMG_BOX.maxWidth,
                height: COMPARE_IMG_BOX.height,
                display: "flex", alignItems: "center", justifyContent: "center",
                textAlign: "center", borderRadius: 8, padding: "0 1em",
                boxSizing: "border-box",
                background: C.greySoft, border: `1px dashed ${C.border}`,
                color: C.textLight, fontSize: "0.85rem",
              }}>
                Keine Referenzbilder zu diesem Kandidaten.
              </div>
            ) : (
              <>
                <div style={{
                  position: "relative",
                  width: "100%", maxWidth: COMPARE_IMG_BOX.maxWidth,
                }}>
                  <img
                    src={current.url}
                    alt={current.label || `Referenz ${idx + 1}`}
                    onClick={() => setZoom(idx)}
                    style={{ ...COMPARE_IMG_BOX, cursor: "zoom-in" }}
                  />
                  {thumbs.length > 1 && (
                    <>
                      <button
                        onClick={() => setIdx((i) => (i - 1 + thumbs.length) % thumbs.length)}
                        aria-label="Vorheriges Referenzbild"
                        style={navBtn("left")}
                      >‹</button>
                      <button
                        onClick={() => setIdx((i) => (i + 1) % thumbs.length)}
                        aria-label="Nächstes Referenzbild"
                        style={navBtn("right")}
                      >›</button>
                    </>
                  )}
                </div>

                <div style={{
                  marginTop: "0.5em", fontSize: "0.85rem",
                  color: C.textStrong, fontWeight: 600,
                }}>{current.label || `Referenz ${idx + 1}`}</div>

                {current.matchedOn.length > 0 && (
                  <div style={{
                    marginTop: "0.35em", display: "flex", gap: "0.35em",
                    flexWrap: "wrap",
                  }}>
                    {current.matchedOn.map((m, i) => (
                      <span key={i} style={{
                        fontSize: "0.68rem", padding: "0.12em 0.5em",
                        borderRadius: 999, background: C.blueSoft,
                        color: "#2a4365", border: "1px solid #90cdf4",
                      }}>{m}</span>
                    ))}
                  </div>
                )}

                {/* Streifen zum direkten Anspringen */}
                {thumbs.length > 1 && (
                  <div style={{
                    marginTop: "0.6em", display: "flex", gap: "0.35em",
                    flexWrap: "wrap",
                  }}>
                    {thumbs.map((t, i) => (
                      <img
                        key={i} src={t.url} alt={t.label || `Referenz ${i + 1}`}
                        onClick={() => setIdx(i)}
                        title={t.label || `Referenz ${i + 1}`}
                        style={{
                          width: 46, height: 46, objectFit: "cover",
                          borderRadius: 4, cursor: "pointer", background: "#fff",
                          border: i === idx
                            ? `2px solid ${C.primaryDark}`
                            : `1px solid ${C.border}`,
                          opacity: i === idx ? 1 : 0.65,
                        }}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Aktionen — dieselbe Sperre wie auf der Karte */}
        <div style={{
          borderTop: `1px solid ${C.border}`, background: C.cardBg,
          padding: "0.9em 1.2em",
        }}>
          <div style={{ display: "flex", gap: "0.6em", alignItems: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => onApprove(cand)}
              disabled={!canSend || busy}
              style={{
                padding: "0.55em 1.3em", borderRadius: 6, border: "none",
                background: canSend && !busy ? C.green : "#cbd5e0",
                color: "#fff", fontWeight: 600, fontSize: "0.9rem",
                cursor: canSend && !busy ? "pointer" : "not-allowed",
              }}
            >{busy ? "Sende…" : "An Kunden senden"}</button>

            <button
              onClick={() => setShowReject((v) => !v)}
              disabled={busy}
              style={{
                padding: "0.55em 1.2em", borderRadius: 6,
                border: `1px solid ${C.border}`, background: "#fff",
                color: C.textStrong, fontSize: "0.88rem",
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >Ablehnen</button>

            <EditorLink candidateId={cand.id} />

            {disabledHint && !canSend && (
              <span style={{
                fontSize: "0.78rem", color: "#7b341e", background: C.amberSoft,
                border: `1px solid ${C.amber}`, padding: "0.35em 0.6em",
                borderRadius: 4,
              }}>{disabledHint}</span>
            )}
          </div>

          {showReject && (
            <div style={{ marginTop: "0.6em", maxWidth: 620 }}>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Kurze Begründung (Pflicht) — z. B. Spruch zu lang, Layout unsauber…"
                rows={2}
                style={{
                  width: "100%", boxSizing: "border-box", padding: "0.5em",
                  border: `1px solid ${C.border}`, borderRadius: 6,
                  fontSize: "0.85rem", fontFamily: "inherit", resize: "vertical",
                }}
              />
              <button
                onClick={() => {
                  onReject(cand, noteDraft.trim());
                  setNoteDraft(""); setShowReject(false);
                }}
                disabled={noteDraft.trim().length < 3 || busy}
                style={{
                  marginTop: "0.4em", padding: "0.45em 1em", borderRadius: 6,
                  border: "none",
                  background: noteDraft.trim().length >= 3 ? C.red : "#cbd5e0",
                  color: "#fff", fontWeight: 600, fontSize: "0.85rem",
                  cursor: noteDraft.trim().length >= 3 ? "pointer" : "not-allowed",
                }}
              >Ablehnung speichern</button>
            </div>
          )}
        </div>
      </div>

      {/* Vollbild */}
      {zoom !== null && (
        <div
          onClick={() => setZoom(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 310, cursor: "zoom-out",
            background: "rgba(0,0,0,0.85)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: "2em",
          }}
        >
          <img
            src={zoom === "draft" ? cand.previewUrl : thumbs[zoom]?.url}
            alt="Vollbild"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </div>
      )}
    </div>
  );
};

// ── Kandidaten-Karte ──────────────────────────────────────────────────────

const CandidateCard = ({ cand, order, orderLoading, onApprove, onReject,
  busyId, draft, draftLoading, history, orderDrafts }) => {
  const [noteDraft, setNoteDraft] = useState("");
  const [showReject, setShowReject] = useState(false);

  const { stage, hasEmail, canSend, disabledHint } =
    computeGating(cand, order, orderLoading);
  const busy = busyId === cand.id;

  const sb = STATUS_BADGE[cand.status] || STATUS_BADGE.superseded;

  // Ein erzwungener Entwurf ist bewusst an einer Regel vorbei entstanden —
  // er soll sich im Reiter nicht unter die gewoehnlichen mischen.
  const forced = cand.forced === true;

  return (
    <div style={{
      background: forced ? "#fffaf0" : C.cardBg,
      border: forced ? "2px solid #dd6b20" : `1px solid ${C.border}`,
      borderRadius: 10,
      padding: "1em 1.15em", display: "flex", gap: "1.15em", flexWrap: "wrap",
    }}>
      {/* Preview */}
      <div style={{ flex: "0 0 210px" }}>
        {cand.previewUrl ? (
          <a href={cand.previewUrl} target="_blank" rel="noopener noreferrer">
            <img src={cand.previewUrl} alt="Entwurfsvorschau" style={{
              width: "100%", borderRadius: 6, border: `1px solid ${C.border}`,
              display: "block",
            }} />
          </a>
        ) : (
          <div style={{
            width: "100%", height: 150, borderRadius: 6, background: C.greySoft,
            border: `1px dashed ${C.border}`, display: "flex",
            alignItems: "center", justifyContent: "center",
            color: C.textLight, fontSize: "0.8rem",
          }}>keine Vorschau</div>
        )}
        <div style={{ marginTop: "0.4em", display: "flex", gap: "0.5em", flexWrap: "wrap" }}>
          {cand.previewUrl && (
            <a href={cand.previewUrl} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: "0.75rem", color: C.primaryDark }}>Vorschau ↗</a>
          )}
          {cand.printUrl && (
            <a href={cand.printUrl} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: "0.75rem", color: C.primaryDark }}>Druckdatei ↗</a>
          )}
        </div>
      </div>

      {/* Details */}
      <div style={{ flex: "1 1 380px", minWidth: 300 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5em", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: C.textStrong }}>
            #{cand.orderId}
            {safeText(order?.buyerName).trim() && (
              <span style={{ fontWeight: 500, color: C.textMuted }}>
                {" · "}{safeText(order.buyerName).trim()}
              </span>
            )}
          </span>
          <Badge {...sb}>{sb.label}</Badge>
          {forced && (
            <Badge bg="#feebc8" color="#7b341e" border="#dd6b20">
              ⚠ Erzwungen
            </Badge>
          )}
          {stage && (
            <Badge bg={C.greySoft} color={C.textMuted} border={C.border}>
              {STAGE_LABELS[stage] || stage}
            </Badge>
          )}
          {cand.belowReference && (
            <Badge bg={C.amberSoft} color="#7b341e" border="#f6ad55"
                   title={cand.belowReferenceLayers?.length
                     ? `Unter Referenz: ${cand.belowReferenceLayers.join(", ")}`
                     : "Gerenderte Größe liegt unter der Referenz"}>
              ⚠ unter Referenz
              {cand.belowReferenceLayers?.length > 0 &&
                <> ({cand.belowReferenceLayers.join(", ")})</>}
            </Badge>
          )}
          {cand.cropMode && cand.cropMode !== "none" && (
            <Badge bg={C.greySoft} color={C.textMuted} border={C.border}
                   title="Zuschnitt-Modus des Renderers">
              Crop: {cand.cropMode}
            </Badge>
          )}
          {!hasEmail && !orderLoading && order && (
            <Badge bg={C.redSoft} color="#742a2a" border="#fc8181">keine E-Mail</Badge>
          )}
        </div>

        <ProductLine order={order} cand={cand} />
        <div style={{ fontSize: "0.78rem", color: C.textMuted, marginTop: "0.3em" }}>
          {cand.templateName || cand.templateId || "—"}
          {cand.listingId && <> · Listing {cand.listingId}</>}
          {" · "}erstellt {fmtTs(cand.createdAt)}
          {cand.modifiedAtMs > 0 && cand.modifiedAtMs !== cand.createdAtMs && (
            <> · geändert {fmtTs(cand.modifiedAt)}</>
          )}
          {cand.renderedByText && <> · {cand.renderedByText}</>}
        </div>

        {cand.reasonList?.length > 0 && (
          <div style={{
            marginTop: "0.5em", fontSize: "0.78rem", color: "#7b341e",
            background: C.amberSoft, border: "1px solid #f6ad55",
            padding: "0.4em 0.6em", borderRadius: 4,
          }}>
            <strong>Hinweise:</strong> {cand.reasonList.join(" · ")}
          </div>
        )}

        <ForcedNotice cand={cand} />
        <CustomerNotice notice={cand.customerNotice} />
        <OverridesLine text={cand.overridesText}
          advanced={cand.overridesAdvanced} />
        <CarriedLine text={cand.carriedText} />
        <ValuesBlock values={cand.valuesObj} />
        <CustomerVoice order={order} drafts={orderDrafts} />
        <FitReport fitReport={cand.fitReportList}
          styleSwitch={cand.styleSwitchText}
          belowLayers={cand.belowReferenceLayers} />

        {cand.reviewNote && (
          <div style={{
            marginTop: "0.6em", fontSize: "0.78rem", color: C.textStrong,
            background: C.greySoft, padding: "0.4em 0.6em", borderRadius: 4,
          }}>
            <strong>Notiz:</strong> {cand.reviewNote}
            {cand.reviewedBy && (
              <span style={{ color: C.textLight }}> — {cand.reviewedBy}, {fmtTs(cand.reviewedAt)}</span>
            )}
          </div>
        )}

        {/* Aktionen nur für pending_review */}
        {cand.status === "pending_review" && (
          <div style={{ marginTop: "0.85em" }}>
            <div style={{ display: "flex", gap: "0.6em", flexWrap: "wrap" }}>
              <button
                onClick={() => onApprove(cand)}
                disabled={!canSend || busy}
                title={disabledHint || "Entwurf an den Kunden senden"}
                style={{
                  padding: "0.5em 1.1em", borderRadius: 6, border: "none",
                  background: canSend && !busy ? C.green : "#cbd5e0",
                  color: "#fff", fontWeight: 600, fontSize: "0.85rem",
                  cursor: canSend && !busy ? "pointer" : "not-allowed",
                }}
              >
                {busy ? "Sende…" : "An Kunden senden"}
              </button>
              <button
                onClick={() => setShowReject((v) => !v)}
                disabled={busy}
                style={{
                  padding: "0.5em 1.1em", borderRadius: 6,
                  border: `1px solid ${C.border}`, background: "#fff",
                  color: C.textStrong, fontSize: "0.85rem",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >Ablehnen</button>
              {/* „Vergleichen" (CompareView mit Referenz-Karussell) ist seit
                  11.09. entfallen: Der Schnellfix-Editor zeigt den
                  Referenzdruck der Grafikerin selbst neben der Live-Ansicht,
                  mit denselben referenceThumbs. Die Ansicht selbst bleibt im
                  Code, ist aber von hier nicht mehr erreichbar. */}
              {canOpenEditor(cand) && (
                <EditorLink candidateId={cand.id} />
              )}
            </div>

            {disabledHint && cand.status === "pending_review" && !canSend && (
              <div style={{
                marginTop: "0.5em", fontSize: "0.76rem", color: "#7b341e",
                background: C.amberSoft, border: "1px solid #f6ad55",
                padding: "0.4em 0.6em", borderRadius: 4,
              }}>{disabledHint}</div>
            )}

            {showReject && (
              <div style={{ marginTop: "0.6em" }}>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Kurze Begründung (Pflicht) — z. B. Spruch zu lang, Layout unsauber…"
                  rows={2}
                  style={{
                    width: "100%", boxSizing: "border-box", padding: "0.5em",
                    border: `1px solid ${C.border}`, borderRadius: 6,
                    fontSize: "0.82rem", fontFamily: "inherit", resize: "vertical",
                  }}
                />
                <button
                  onClick={() => { onReject(cand, noteDraft.trim()); setNoteDraft(""); setShowReject(false); }}
                  disabled={noteDraft.trim().length < 3 || busy}
                  style={{
                    marginTop: "0.4em", padding: "0.45em 1em", borderRadius: 6,
                    border: "none",
                    background: noteDraft.trim().length >= 3 ? C.red : "#cbd5e0",
                    color: "#fff", fontWeight: 600, fontSize: "0.82rem",
                    cursor: noteDraft.trim().length >= 3 ? "pointer" : "not-allowed",
                  }}
                >Ablehnung speichern</button>
              </div>
            )}
          </div>
        )}

        {/* An Kunden gesendet: Kundenstatus + Druckdatei erst nach Freigabe.
            Der zugehörige Draft liegt unter drafts/{candidateId} (ID-Kopplung). */}
        {cand.status === "approved" && (
          <DraftOutcome cand={cand} draft={draft} draftLoading={draftLoading} />
        )}

        <VersionHistory history={history} />
      </div>
    </div>
  );
};

// ── needs_manual: read-only Karte ─────────────────────────────────────────

/**
 * Wie lange auf den Watcher gewartet wird, bevor die Karte einen Hinweis
 * zeigt und den Knopf wieder freigibt. Der Watcher antwortet normalerweise
 * in Sekunden; laeuft das Personalization Tool auf dem PC nicht, kaeme sonst
 * nie eine Antwort und die Karte haengt fuer immer auf „angefordert …".
 */
const FORCE_AUTO_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * Der Knopf „Auto-Entwurf erzwingen" samt Rueckmeldung des Watchers.
 * Steht nur auf Kandidaten mit status === "needs_manual".
 */
const ForceAutoBox = ({ cand, onForce, onCancel, busy }) => {
  // Tickt, damit der Timeout-Hinweis ohne neues Firestore-Ereignis erscheint.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!cand.forceAutoPending) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 10000);
    return () => clearInterval(t);
  }, [cand.forceAutoPending]);

  const waitedMs = cand.forceAutoRequestedAtMs
    ? Date.now() - cand.forceAutoRequestedAtMs : 0;
  const stalled = cand.forceAutoPending &&
    cand.forceAutoRequestedAtMs > 0 && waitedMs > FORCE_AUTO_TIMEOUT_MS;
  const kind = forceAutoResultKind(cand.forceAutoResult);
  const rejected = kind === "rejected" || kind === "rejected_variant";
  const aboutVariant = kind === "rejected_variant";

  return (
    <div style={{ marginTop: "0.6em" }}>
      <div style={{ display: "flex", gap: "0.6em", alignItems: "center", flexWrap: "wrap" }}>
        <button
          onClick={() => onForce(cand)}
          disabled={busy || (cand.forceAutoPending && !stalled)}
          title={"Bittet das Personalization Tool, für diese Bestellzeile " +
            "trotzdem einen Entwurf zu rendern. Fehlende Pflichtwerte " +
            "erscheinen als xxxxx und müssen im Schnellfix ersetzt werden — " +
            "bis dahin ist die Freigabe gesperrt. Mehrfaches Anfordern " +
            "erzeugt keinen zweiten Entwurf."}
          style={{
            border: "none", borderRadius: 6, padding: "0.4em 0.9em",
            background: (cand.forceAutoPending && !stalled) ? C.greySoft : "#2b6cb0",
            color: (cand.forceAutoPending && !stalled) ? C.textMuted : "#fff",
            fontWeight: 600, fontSize: "0.82rem",
            cursor: (busy || (cand.forceAutoPending && !stalled))
              ? "not-allowed" : "pointer",
          }}
        >
          {cand.forceAutoPending && !stalled
            ? "angefordert …"
            : (cand.forceAutoResult ? "Erneut erzwingen" : "Auto-Entwurf erzwingen")}
        </button>
        {cand.forceAutoPending && !stalled && cand.forceAutoBy && (
          <span style={{ fontSize: "0.75rem", color: C.textLight }}>
            von {cand.forceAutoBy}
          </span>
        )}
      </div>

      {stalled && (
        <div style={{
          marginTop: "0.45em", fontSize: "0.78rem", color: "#744210",
          background: "#fffbeb", border: "1px solid #f6e05e",
          borderRadius: 4, padding: "0.4em 0.6em",
        }}>
          ⏱ Keine Antwort seit über 2 Minuten — läuft das Personalization
          Tool auf dem PC? <strong>Die Anfrage bleibt stehen</strong> und wird
          nachgeholt, sobald das Tool startet. Ein erneuter Klick schadet
          nicht (es entsteht kein zweiter Entwurf), oder{" "}
          <button
            onClick={() => onCancel(cand)}
            disabled={busy}
            style={{
              border: "none", background: "none", padding: 0,
              color: "#2b6cb0", textDecoration: "underline",
              fontSize: "0.78rem", cursor: busy ? "not-allowed" : "pointer",
            }}
          >die Anfrage zurücknehmen</button>, damit sie später nicht doch noch
          ausgeführt wird.
        </div>
      )}

      {!cand.forceAutoPending && cand.forceAutoResult && (
        <div style={{
          marginTop: "0.45em", fontSize: "0.78rem",
          color: rejected ? "#742a2a" : "#22543d",
          background: rejected ? "#fff5f5" : "#f0fff4",
          border: `1px solid ${rejected ? "#fc8181" : "#9ae6b4"}`,
          borderRadius: 4, padding: "0.4em 0.6em",
        }}>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {rejected ? "✗ " : "✓ "}{cand.forceAutoResult}
          </div>
          {aboutVariant && (
            <div style={{ marginTop: "0.25em", color: C.textMuted }}>
              Das muss zuerst im Image Generator entschieden werden.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ManualCard = ({ cand, order, history, orderDrafts, onForce,
  onCancelForce, busy }) => {
  const stage = order?.workflowStage;
  return (
    <div style={{
      background: C.cardBg, border: `1px solid #90cdf4`, borderRadius: 10,
      padding: "0.85em 1.1em", display: "flex", gap: "1em", flexWrap: "wrap",
    }}>
      <div style={{ flex: "1 1 420px", minWidth: 280 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5em", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: C.textStrong }}>
            #{cand.orderId}
            {safeText(order?.buyerName).trim() && (
              <span style={{ fontWeight: 500, color: C.textMuted }}>
                {" · "}{safeText(order.buyerName).trim()}
              </span>
            )}
          </span>
          <Badge {...STATUS_BADGE.needs_manual}>Manuell nötig</Badge>
          {stage && (
            <Badge bg={C.greySoft} color={C.textMuted} border={C.border}>
              {STAGE_LABELS[stage] || stage}
            </Badge>
          )}
        </div>
        <ProductLine order={order} cand={cand} />
        <div style={{ fontSize: "0.78rem", color: C.textMuted, marginTop: "0.25em" }}>
          {cand.templateName || cand.templateId || "—"}
          {cand.listingId && <> · Listing {cand.listingId}</>}
          {" · "}{fmtTs(cand.createdAt)}
          {cand.renderedByText && <> · {cand.renderedByText}</>}
        </div>
        <div style={{
          marginTop: "0.5em", fontSize: "0.82rem", color: "#2a4365",
          background: C.blueSoft, border: "1px solid #90cdf4",
          padding: "0.45em 0.65em", borderRadius: 4,
        }}>
          {/* Der Renderer schreibt hier seit 11.09. ganze Saetze: was fehlt,
              warum, und wo es im Etsy-Backend zu beheben ist. Vollstaendig
              anzeigen und umbrechen lassen — gekuerzt waere genau der
              Handlungsteil weg, der am Ende steht. */}
          <strong>Grund:</strong>{" "}
          <span style={{ whiteSpace: "pre-wrap" }}>
            {cand.manualReason || cand.reasonList?.join(" · ") ||
              "— (kein Grund angegeben)"}
          </span>
        </div>
        <CustomerNotice notice={cand.customerNotice} />
        <OverridesLine text={cand.overridesText}
          advanced={cand.overridesAdvanced} />
        <CarriedLine text={cand.carriedText} />
        <ValuesBlock values={cand.valuesObj} />
        <CustomerVoice order={order} drafts={orderDrafts} />
        <FitReport fitReport={cand.fitReportList}
          styleSwitch={cand.styleSwitchText}
          belowLayers={cand.belowReferenceLayers} />
        <ForceAutoBox cand={cand} onForce={onForce}
          onCancel={onCancelForce} busy={busy} />
        <VersionHistory history={history} />
      </div>
      {cand.previewUrl && (
        <div style={{ flex: "0 0 150px" }}>
          <a href={cand.previewUrl} target="_blank" rel="noopener noreferrer">
            <img src={cand.previewUrl} alt="Vorschau" style={{
              width: "100%", borderRadius: 6, border: `1px solid ${C.border}`,
            }} />
          </a>
        </div>
      )}
    </div>
  );
};

// Diese Status gelten als "aktuell" — der neueste davon fuehrt seine Kette an.

/**
 * Ein Eintrag im Archiv. Kompakt, weil hier nur noch nachgeschlagen und
 * aufgeräumt wird.
 *
 * Gelöscht werden darf nur, was den Kunden nie erreicht hat. Ging der
 * Kandidat als Entwurf raus, bleibt er als Beleg stehen — dafür steht das
 * drafts/{candidateId}-Dokument (`sent`).
 */
const ArchiveEntry = ({ cand, sent, onDelete, deletingId }) => {
  const [asking, setAsking] = useState(false);
  const meta = STATUS_BADGE[cand.status] || STATUS_BADGE.superseded;
  const busy = deletingId === cand.id;

  return (
    <div style={{
      display: "flex", gap: "0.8em", alignItems: "flex-start",
      background: C.cardBg, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: "0.6em 0.8em",
    }}>
      {cand.previewUrl && (
        <a href={cand.previewUrl} target="_blank" rel="noopener noreferrer">
          <img src={cand.previewUrl} alt="" style={{
            width: 54, height: 54, objectFit: "contain", borderRadius: 4,
            background: "#fff", border: `1px solid ${C.border}`, display: "block",
          }} />
        </a>
      )}

      <div style={{ flex: "1 1 300px", minWidth: 220 }}>
        <div style={{ display: "flex", gap: "0.5em", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: C.textStrong }}>#{cand.orderId}</span>
          <Badge {...meta}>{meta.label}</Badge>
          <span style={{ fontSize: "0.75rem", color: C.textMuted }}>
            {fmtTs(cand.createdAt)}
          </span>
        </div>
        <div style={{ fontSize: "0.78rem", color: C.textMuted, marginTop: "0.2em" }}>
          {cand.templateName || cand.templateId || "—"}
        </div>
        {cand.obsoleteReasonLabel && (
          <div style={{ fontSize: "0.76rem", color: C.textMuted, marginTop: "0.2em" }}>
            Grund: {cand.obsoleteReasonLabel}
          </div>
        )}
        {cand.status === "rejected" && cand.reviewNote && (
          <div style={{ fontSize: "0.76rem", color: "#742a2a", marginTop: "0.2em" }}>
            Abgelehnt: {cand.reviewNote}
          </div>
        )}
      </div>

      <div style={{
        display: "flex", gap: "0.7em", alignItems: "center",
        flexWrap: "wrap", justifyContent: "flex-end",
      }}>
        {cand.previewUrl && (
          <a href={cand.previewUrl} target="_blank" rel="noopener noreferrer"
             style={{ fontSize: "0.78rem", color: C.primaryDark }}>Vorschau ↗</a>
        )}
        {cand.printUrl && (
          <a href={cand.printUrl} target="_blank" rel="noopener noreferrer"
             style={{ fontSize: "0.78rem", color: C.primaryDark }}>Druckdatei ↗</a>
        )}

        {sent === undefined ? (
          <span style={{ fontSize: "0.75rem", color: C.textLight }}>prüfe…</span>
        ) : sent ? (
          <span
            title="Dieser Kandidat wurde als Entwurf an den Kunden gesendet und bleibt als Beleg erhalten."
            style={{ fontSize: "0.75rem", color: C.textLight }}
          >gesendet · bleibt als Historie</span>
        ) : !DELETABLE_STATUSES.has(cand.status) ? (
          <span style={{ fontSize: "0.75rem", color: C.textLight }}>
            nicht löschbar
          </span>
        ) : asking ? (
          <>
            <span style={{ fontSize: "0.75rem", color: "#742a2a" }}>
              Endgültig löschen?
            </span>
            <button
              onClick={() => { setAsking(false); onDelete(cand); }}
              disabled={busy}
              style={{
                border: "none", borderRadius: 4, padding: "0.2em 0.6em",
                background: C.red, color: "#fff", fontWeight: 600,
                fontSize: "0.75rem", cursor: busy ? "not-allowed" : "pointer",
              }}
            >{busy ? "…" : "Ja, löschen"}</button>
            <button
              onClick={() => setAsking(false)}
              style={{
                border: `1px solid ${C.border}`, borderRadius: 4,
                padding: "0.2em 0.6em", background: "#fff",
                color: C.textStrong, fontSize: "0.75rem", cursor: "pointer",
              }}
            >Abbrechen</button>
          </>
        ) : (
          <button
            onClick={() => setAsking(true)}
            disabled={busy}
            title="Dokument, Vorschau und Druckdatei endgültig entfernen (Referenzbilder bleiben)"
            style={{
              border: `1px solid ${C.border}`, borderRadius: 4,
              padding: "0.2em 0.6em", background: "#fff",
              color: C.red, fontSize: "0.75rem",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >{busy ? "Löscht…" : "Löschen"}</button>
        )}
      </div>
    </div>
  );
};

const ACTIVE_STATUSES = new Set(["pending_review", "needs_manual", "approved"]);

// Reihenfolge der Etsy-Stages. "ueber Grafiker hinaus" heisst: der Entwurf
// ist beim Kunden oder die Bestellung ist schon in Produktion — ein
// Kandidat, der noch auf die Grafik wartet, ist dann gegenstandslos.
const STAGE_RANK = {
  new: 0, grafiker: 1, entwurf_raus: 2,
  produktion: 3, druck: 4, kerzen: 5, archived: 9,
};
const beyondGrafiker = (order) => {
  const r = STAGE_RANK[order?.workflowStage];
  return r !== undefined && r > 1;
};

/**
 * Gehoert ein `needs_manual`-Kandidat ins Archiv?
 * Normalerweise ja, sobald die Bestellung den Grafiker-Schritt hinter sich hat.
 * AUSSER die Kundin hat den Entwurf zurueckgeschickt: ein Aenderungswunsch
 * entsteht erst, wenn der Entwurf raus ist (Stufe entwurf_raus = Rang 2).
 * Hat die Grafikerin den Entwurf von Hand gemacht, gibt es keinen
 * freigegebenen Kandidaten — der `needs_manual` des Tools ist dann die einzige
 * Spur des Wunsches und darf nicht im Archiv verschwinden (#4170656735,
 * 15.09.2026). Ab Produktion ist der Fall erledigt, dann wieder Archiv.
 */
export const manualHeadBelongsToArchive = (order) => {
  if (!beyondGrafiker(order)) return false;
  const returned = order?.draftDecision === "modification" &&
    STAGE_RANK[order?.workflowStage] <= STAGE_RANK.entwurf_raus;
  return !returned;
};

// Nur diese Versionen duerfen von Hand geloescht werden.
// Nur diese Status duerfen aus dem Archiv geloescht werden — und auch das
// nur, wenn der Kandidat nie beim Kunden war (kein drafts/{id}-Dokument).
const DELETABLE_STATUSES = new Set(["superseded", "rejected", "obsolete"]);

// Diese Status landen immer im Archiv.
const ARCHIVE_STATUSES = new Set(["superseded", "rejected", "obsolete"]);

// obsoleteReason ist ein Schluessel, kein Anzeigetext — der Renderer laesst
// nur diese beiden Werte (oder null) zu. Unbekanntes wird roh angezeigt statt
// verschluckt, damit ein neuer Schluessel sofort auffaellt.
const OBSOLETE_REASON_LABELS = {
  manual_draft_exists:         "Manueller Entwurf vorhanden",
  stage_advanced:              "Bestellung bereits weiter",
  // Seit 15.09.2026 vom Tool geschrieben (Stand im Bestand gezaehlt):
  returned_draft_is_the_basis: "Zurückgeschickter Entwurf ist die Grundlage",
  duplicate:                   "Doppelt angelegt",
};
const obsoleteReasonLabel = (key) => {
  const k = safeText(key).trim();
  if (!k) return "";
  return OBSOLETE_REASON_LABELS[k] || k;
};

const EMPTY_HINT = {
  pending:  "Keine offenen Kandidaten.",
  customer: "Kein Entwurf wartet gerade auf eine Kundenrückmeldung.",
  changes:  "Kein offener Änderungswunsch.",
  released: "Noch nichts vom Kunden freigegeben.",
  manual:   "Nichts, was die Grafik übernehmen müsste.",
  archive:  "Archiv ist leer.",
};

const TABS = [
  { key: "pending",  label: "Prüfung offen" },
  { key: "customer", label: "Beim Kunden" },
  { key: "changes",  label: "Änderungsbedarf" },
  { key: "released", label: "Freigegeben" },
  { key: "manual",   label: "Manuell nötig" },
  { key: "archive",  label: "Archiv" },
];

// ── Hauptseite ────────────────────────────────────────────────────────────

const DraftCandidatesPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState({});      // orderId -> data | null
  const [ordersLoading, setOrdersLoading] = useState({});
  // orderId -> alle drafts/* der Bestellung (Abfrage ueber orderNumber).
  // Fuer den Block „Kunde schreibt": Kommentare zu jedem Entwurf, auch zu
  // manuellen der Grafikerin — nicht nur zum Kandidaten selbst.
  const [draftsByOrder, setDraftsByOrder] = useState({});
  const [activeTab, setActiveTab] = useState("pending");
  // id -> true (war beim Kunden) | false (nie gesendet) | undefined (unklar)
  const [sentMap, setSentMap] = useState({});
  const [askClear, setAskClear] = useState(false);
  const [clearingArchive, setClearingArchive] = useState(false);
  const [clearProgress, setClearProgress] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [forcingId, setForcingId] = useState(null);
  const [deletingVersionId, setDeletingVersionId] = useState(null);
  // Prüfansicht: Kandidaten-ID oder null. Rein intern — die
  // Referenzbilder zeigen Arbeiten anderer Kunden.
  const [compareId, setCompareId] = useState(null);
  const [toast, setToast] = useState(null);
  const [drafts, setDrafts] = useState({});          // candidateId -> data | null
  const [draftsLoading, setDraftsLoading] = useState({});

  // Kandidaten live
  useEffect(() => {
    // Kein orderBy in der Query: createdAt kann kurzzeitig null sein
    // (pending serverTimestamp) — solche Docs würden von einer
    // orderBy-Query ausgeschlossen. Sortierung passiert client-seitig.
    const q = query(collection(db, "draft_candidates"));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => {
        try {
          return normalizeCandidate({ id: d.id, ...d.data() });
        } catch (e) {
          console.error("normalizeCandidate failed for", d.id, e);
          return { id: d.id, status: "pending_review", orderId: "",
                   _normalizeError: String(e?.message || e) };
        }
      });
      // Neueste zuerst; Docs ohne createdAt (noch pending) nach oben,
      // damit sie nicht unbemerkt ans Listenende rutschen.
      rows.sort((a, b) => {
        const am = a.createdAtMs || 0;
        const bm = b.createdAtMs || 0;
        if (am === 0 && bm === 0) return 0;
        if (am === 0) return -1;
        if (bm === 0) return 1;
        return bm - am;
      });
      setCandidates(rows);
      setLoading(false);
    }, (err) => {
      console.error("draft_candidates subscribe error", err);
      setToast({ type: "error", msg: "Kandidaten konnten nicht geladen werden." });
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Zugehörige Orders nachladen (einmal pro orderId)
  useEffect(() => {
    const needed = [...new Set(candidates.map((c) => c.orderId).filter(Boolean))];
    needed.forEach((oid) => {
      if (oid in orders || ordersLoading[oid]) return;
      setOrdersLoading((p) => ({ ...p, [oid]: true }));
      getDoc(doc(db, "etsy_orders", String(oid)))
        .then((s) => setOrders((p) => ({ ...p, [oid]: s.exists() ? s.data() : null })))
        .catch(() => setOrders((p) => ({ ...p, [oid]: null })))
        .finally(() => setOrdersLoading((p) => ({ ...p, [oid]: false })));
    });
  }, [candidates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alle Entwuerfe je sichtbarer Bestellung live mitlesen — Quelle fuer die
  // Kundenkommentare bei „Entwurf bestaetigen". Ein Listener pro Bestellung,
  // nicht pro Kandidat; ueber orderNumber, damit auch manuelle Entwuerfe der
  // Grafikerin dabei sind. Stabiler Key, damit die Listener nicht bei jedem
  // Snapshot neu aufgesetzt werden.
  const orderIdsKey = useMemo(
    () => Array.from(new Set(candidates.map((c) => c.orderId).filter(Boolean)))
        .sort().join(","),
    [candidates],
  );
  useEffect(() => {
    const ids = orderIdsKey ? orderIdsKey.split(",") : [];
    const unsubs = ids.map((oid) => onSnapshot(
        query(collection(db, "drafts"), where("orderNumber", "==", String(oid))),
        (snap) => {
          const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setDraftsByOrder((p) => ({ ...p, [oid]: rows }));
        },
        () => setDraftsByOrder((p) => ({ ...p, [oid]: [] })),
    ));
    return () => unsubs.forEach((u) => u());
  }, [orderIdsKey]);

  // Für freigegebene Kandidaten den zugehörigen Draft live mitlesen —
  // dort steht die Kundenentscheidung, die den Print-Download freischaltet.
  // Draft-ID == Candidate-ID (ID-Kopplung aus der Freigabe-Aktion).
  // Stabiler Key, damit die Listener nicht bei jedem Kandidaten-Snapshot
  // neu aufgesetzt werden.
  const approvedIdsKey = useMemo(
    () => candidates.filter((c) => c.status === "approved")
                    .map((c) => c.id).sort().join(","),
    [candidates],
  );

  useEffect(() => {
    const approvedIds = approvedIdsKey ? approvedIdsKey.split(",") : [];
    const unsubs = approvedIds.map((cid) => {
      setDraftsLoading((p) => (p[cid] ? p : { ...p, [cid]: true }));
      return onSnapshot(doc(db, "drafts", cid), (snap) => {
        setDrafts((p) => ({ ...p, [cid]: snap.exists() ? snap.data() : null }));
        setDraftsLoading((p) => ({ ...p, [cid]: false }));
      }, () => {
        setDrafts((p) => ({ ...p, [cid]: null }));
        setDraftsLoading((p) => ({ ...p, [cid]: false }));
      });
    });
    return () => unsubs.forEach((u) => u());
  }, [approvedIdsKey]);

  // Kandidaten einer Korrekturkette zusammenfassen und auf die Reiter
  // verteilen.
  //
  // Kette: massgeblich ist parentCandidateId (bis zur Wurzel hochgelaufen,
  // zyklussicher), ersatzweise das Doc-ID-Praefix {orderId}_{transactionId}.
  //
  // Reiter: der Status entscheidet grob, bei "approved" zusaetzlich der
  // Entwurf beim Kunden — deshalb haengt die Einordnung am drafts-Dokument.
  // Solange das noch laedt, steht der Kandidat unter "Beim Kunden"; das ist
  // der Zustand direkt nach dem Senden und damit die richtige Annahme.
  const { tabs, historyByHead } = useMemo(() => {
    const prefixKey = (c) => {
      const parts = String(c.id).split("_");
      return parts.length > 1
        ? parts.slice(0, -1).join("_")
        : (c.orderId || c.id);
    };

    const byId = new Map(candidates.map((c) => [c.id, c]));

    const rootOf = (c) => {
      const visited = new Set();
      let cur = c;
      while (cur.parentCandidateId && !visited.has(cur.id)) {
        visited.add(cur.id);
        const parent = byId.get(cur.parentCandidateId);
        if (!parent) break;
        cur = parent;
      }
      return cur;
    };

    const chains = new Map();
    for (const c of candidates) {
      const k = prefixKey(rootOf(c));
      if (!chains.has(k)) chains.set(k, []);
      chains.get(k).push(c);   // candidates ist bereits neueste-zuerst
    }

    const t = {
      pending: [], customer: [], changes: [],
      released: [], manual: [], archive: [],
    };
    const hist = {};

    for (const members of chains.values()) {
      const head = members.find((m) => ACTIVE_STATUSES.has(m.status))
                || members[0];
      hist[head.id] = members.filter((m) => m.id !== head.id);

      // Alle nicht-aktuellen Mitglieder der Kette gehoeren ins Archiv,
      // sofern ihr Status das hergibt.
      for (const m of members) {
        if (m.id !== head.id && ARCHIVE_STATUSES.has(m.status)) t.archive.push(m);
      }

      const order = orders[head.orderId];

      if (ARCHIVE_STATUSES.has(head.status)) {
        t.archive.push(head);
      } else if (head.status === "pending_review") {
        t.pending.push(head);
      } else if (head.status === "needs_manual") {
        // Hat die Bestellung den Grafiker-Schritt hinter sich, ist der
        // Kandidat gegenstandslos — dann ins Archiv statt in die Arbeitsliste.
        if (manualHeadBelongsToArchive(order)) t.archive.push(head);
        else t.manual.push(head);
      } else if (head.status === "approved") {
        const d = drafts[head.id];
        const decision = d?.customerDecision;
        if (decision === "modification") t.changes.push(head);
        else if (decision === "approved") t.released.push(head);
        else t.customer.push(head);
      } else {
        t.archive.push(head);   // unbekannter Status: sichtbar, nicht verloren
      }
    }

    // Archiv: neueste zuerst, unabhaengig von der Kettenreihenfolge.
    t.archive.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));

    return { tabs: t, historyByHead: hist };
  }, [candidates, orders, drafts]);

  // Loeschbar ist nur, was nie beim Kunden war. Fuer das Archiv wird deshalb
  // je Eintrag einmal geprueft, ob es ein drafts/{candidateId} gibt.
  const archiveIdsKey = useMemo(
    () => tabs.archive.map((c) => c.id).sort().join(","),
    [tabs.archive],
  );

  useEffect(() => {
    if (activeTab !== "archive") return;
    const ids = archiveIdsKey ? archiveIdsKey.split(",") : [];
    let cancelled = false;
    (async () => {
      for (const id of ids) {
        if (sentMap[id] !== undefined) continue;
        try {
          const snap = await getDoc(doc(db, "drafts", id));
          if (cancelled) return;
          setSentMap((m) => ({ ...m, [id]: snap.exists() }));
        } catch (e) {
          console.warn("Entwurfs-Pruefung fehlgeschlagen fuer", id, e);
          if (cancelled) return;
          // Unklar heisst: nicht anbieten. Lieber nichts loeschen.
          setSentMap((m) => ({ ...m, [id]: true }));
        }
      }
    })();
    return () => { cancelled = true; };
    // sentMap bewusst nicht in den Abhaengigkeiten: der Effekt fuellt es
    // selbst und wuerde sich sonst bei jedem Eintrag neu starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, archiveIdsKey]);

  const deletableArchive = useMemo(
    () => tabs.archive.filter(
      (c) => DELETABLE_STATUSES.has(c.status) && sentMap[c.id] === false),
    [tabs.archive, sentMap],
  );

  // ── Freigabe: Draft im bestehenden Kundenpfad anlegen ───────────────────
  const handleApprove = async (cand) => {
    setBusyId(cand.id);
    setToast(null);
    try {
      const order = orders[cand.orderId];
      if (!order) throw new Error(`Bestellung ${cand.orderId} nicht gefunden.`);
      if (!order.customerEmail) throw new Error("Keine E-Mail-Adresse an der Bestellung.");
      const stage = order.workflowStage || "new";
      if (!STAGE_OK_FOR_SEND.has(stage)) {
        throw new Error(`Bestellung ist noch in Stage "${stage}".`);
      }
      if (!cand.previewUrl) throw new Error("Kandidat hat keine previewUrl.");
      // Zweite Sperre neben computeGating: der Knopf ist zwar deaktiviert,
      // aber zwischen Anzeige und Klick kann sich der Kandidat geaendert
      // haben (der Watcher schreibt live). Ein xxxxx-Entwurf geht nicht raus.
      if (Array.isArray(cand.forcedPlaceholders)
          && cand.forcedPlaceholders.length) {
        throw new Error(
          "Entwurf enthält noch Platzhalter (xxxxx): " +
          `${cand.forcedPlaceholders.join(", ")}. ` +
          "Bitte zuerst im lokalen Schnellfix ersetzen.");
      }

      const auth = getAuth();
      const who = auth.currentUser?.displayName || auth.currentUser?.email || "Admin";

      // Idempotenz Teil 1: Draft-ID = Candidate-ID → nur ein Draft möglich.
      const draftRef = doc(db, "drafts", cand.id);
      const existing = await getDoc(draftRef);

      // Idempotenz Teil 2: Statuswechsel transaktional, nur aus pending_review.
      await runTransaction(db, async (tx) => {
        const candRef = doc(db, "draft_candidates", cand.id);
        const fresh = await tx.get(candRef);
        if (!fresh.exists()) throw new Error("Kandidat existiert nicht mehr.");
        if (fresh.data().status !== "pending_review") {
          throw new Error(
            `Kandidat wurde zwischenzeitlich auf "${fresh.data().status}" gesetzt.`);
        }
        tx.update(candRef, {
          status: "approved",
          reviewedAt: serverTimestamp(),
          reviewedBy: who,
          reviewNote: cand.reviewNote || null,
        });
      });

      // Draft nur anlegen wenn noch keiner da ist (Wiederholung nach Teilfehler).
      if (!existing.exists()) {
        const firstName =
          order.confirmedFirstName ||
          order.suggestedFirstName ||
          (order.buyerName || "").split(" ")[0] ||
          null;

        // Exakt dieselben Felder wie der Single-Image-Pfad in AdminUploadPage.
        await setDoc(draftRef, {
          customerEmail:     order.customerEmail,
          customerFirstName: firstName,
          orderNumber:       String(cand.orderId),
          imageUrl:          cand.previewUrl,
          storagePath:       cand.previewStoragePath || null,
          uploaderIp:        "auto:draft_candidates",
          createdAt:         serverTimestamp(),
          // Hinweis des Renderers wandert mit: Review-Seite zeigt ihn ueber
          // dem Bild, die erste E-Mail rendert ihn als {{HINWEIS}}.
          customerNotice:    cand.customerNotice || null,
          // Fuer das Reporting „automatisiert vs. manuell": hier, bei der
          // Freigabe, steht fest, ob im Schnellfix korrigiert wurde. Das
          // Reporting muss dafuer die (grossen, wachsenden) Kandidaten nicht
          // laden, und cleanupReviewedDrafts traegt es in draft_metrics weiter.
          autoCorrected:     hasRealCorrections(cand.overrides),
        });
      }

      setToast({
        type: "ok",
        msg: existing.exists()
          ? `Kandidat freigegeben — Entwurf für #${cand.orderId} existierte bereits (kein Doppelversand).`
          : `Entwurf für #${cand.orderId} an den Kunden gesendet.`,
      });
    } catch (err) {
      console.error("approve failed", err);
      setToast({ type: "error", msg: `Freigabe fehlgeschlagen: ${err.message}` });
    } finally {
      setBusyId(null);
    }
  };

  // ── Ablehnung ──────────────────────────────────────────────────────────
  const handleReject = async (cand, note) => {
    if (!note || note.length < 3) return;
    setBusyId(cand.id);
    setToast(null);
    try {
      const auth = getAuth();
      const who = auth.currentUser?.displayName || auth.currentUser?.email || "Admin";

      await runTransaction(db, async (tx) => {
        const candRef = doc(db, "draft_candidates", cand.id);
        const fresh = await tx.get(candRef);
        if (!fresh.exists()) throw new Error("Kandidat existiert nicht mehr.");
        if (fresh.data().status !== "pending_review") {
          throw new Error(
            `Kandidat wurde zwischenzeitlich auf "${fresh.data().status}" gesetzt.`);
        }
        tx.update(candRef, {
          status: "rejected",
          reviewedAt: serverTimestamp(),
          reviewedBy: who,
          reviewNote: note,
        });
      });

      // Note-Event auf der Bestellung — Order bleibt in ihrer Stage.
      if (cand.orderId) {
        try {
          await addDoc(collection(db, "note_events_queue"), {
            orderId: String(cand.orderId),
            text: `Auto-Entwurf abgelehnt: ${note}`,
            status: "pending",
            createdAt: Timestamp.now(),
            source: "draft_candidate_rejected",
          });
        } catch (e) {
          console.warn("note_events_queue write failed", e);
        }
      }

      setToast({ type: "ok", msg: `Kandidat für #${cand.orderId} abgelehnt.` });
    } catch (err) {
      console.error("reject failed", err);
      setToast({ type: "error", msg: `Ablehnung fehlgeschlagen: ${err.message}` });
    } finally {
      setBusyId(null);
    }
  };

  // ── „Auto-Entwurf erzwingen" ────────────────────────────────────────────
  // Bittet den Personalization-Watcher, fuer eine als manuell markierte
  // Bestellzeile trotzdem zu rendern. Diese App rendert nichts und setzt
  // keinen Status — sie schreibt nur die Anfrage und liest die Antwort, die
  // der Watcher auf denselben Kandidaten legt (forceAutoResult, status).
  const handleForceAuto = async (cand) => {
    setForcingId(cand.id);
    setToast(null);
    try {
      const auth = getAuth();
      const who = auth.currentUser?.displayName ||
        auth.currentUser?.email || "Admin";
      await updateDoc(doc(db, "draft_candidates", cand.id), {
        forceAutoPending: true,
        forceAuto: { by: who, requestedAt: serverTimestamp() },
      });
      // Das Ergebnis kommt ueber onSnapshot herein; hier nichts weiter tun.
    } catch (e) {
      setToast({
        kind: "error",
        text: `Anfrage fehlgeschlagen: ${e.message}`,
      });
    } finally {
      setForcingId(null);
    }
  };

  // Zuruecksetzen, wenn der Watcher nicht antwortet — sonst haengt die Karte
  // dauerhaft auf „angefordert …". Das Personalization Tool laeuft lokal auf
  // einem PC; ist es aus, kommt nie eine Antwort.
  const handleCancelForceAuto = async (cand) => {
    setForcingId(cand.id);
    setToast(null);
    try {
      await updateDoc(doc(db, "draft_candidates", cand.id), {
        forceAutoPending: false,
      });
    } catch (e) {
      setToast({ kind: "error", text: `Zurücksetzen fehlgeschlagen: ${e.message}` });
    } finally {
      setForcingId(null);
    }
  };

  // ── Alte Version aus der Ansicht entfernen ──────────────────────────────
  // Loescht NUR das Firestore-Dokument. Die Bilder gehoeren dem
  // Personalization Tool — Begruendung unten an der Stelle selbst.
  const handleDeleteCandidate = async (cand, silent = false) => {
    setDeletingVersionId(cand.id);
    setToast(null);
    try {
      if (!DELETABLE_STATUSES.has(cand.status)) {
        throw new Error(
          `Nur archivierte Kandidaten können gelöscht werden ` +
          `(Status: ${cand.status}).`);
      }

      // Sicherheitsnetz: ging diese Version schon an den Kunden, zeigt sein
      // Entwurfs-Link auf genau diese Dateien.
      const draftSnap = await getDoc(doc(db, "drafts", cand.id));
      if (draftSnap.exists()) {
        throw new Error(
          "Diese Version wurde bereits an den Kunden gesendet — sein " +
          "Entwurfs-Link zeigt auf diese Dateien. Nicht gelöscht.");
      }

      // Die Dateien unter candidates/ ruehren wir NICHT an — auch nicht die
      // Vorschau und die Druckdatei dieses Kandidaten. Sie gehoeren dem
      // Personalization Tool, das sie selbst aufraeumt
      // (tools/storage_orphans: nur Praefix candidates/, nichts juenger als
      // eine Stunde, nichts ohne --apply, und ein Lauf ohne gefundene
      // Referenzen wird abgewiesen).
      //
      // Bis 14.09.2026 loeschte diese Stelle Vorschau + Druckdatei mit. Sie
      // war die letzte Ausnahme von der Regel „jeder loescht nur, was er
      // selbst hochgeladen hat" (isOwnDraftFile in DraftOverview und in den
      // Cloud Functions) — und eine Ausnahme, an die sich niemand erinnert,
      // ist genau die, die irgendwann zuschlaegt: am selben Tag hatte schon
      // das Archivieren eines Entwurfs eine fremde Vorschau vernichtet.
      // Drueben ist ein `superseded`-Kandidat kein Abfall, sondern der Beleg
      // dafuer, was ersetzt wurde; die Kette ueber `supersededBy` ist nur
      // etwas wert, solange das ersetzte Bild noch da ist.
      //
      // Das Dokument entfernen wir weiterhin: es ist unsere Ansicht, und ohne
      // Dokument nennt niemand mehr die Pfade — die Aufraeumung drueben holt
      // sie dann ab.
      await deleteDoc(doc(db, "draft_candidates", cand.id));
      setSentMap((m) => {
        const next = { ...m };
        delete next[cand.id];
        return next;
      });
      if (!silent) {
        setToast({ type: "ok",
          msg: `Kandidat #${cand.orderId} vom ${fmtTs(cand.createdAt)} gelöscht.` });
      }
      return true;
    } catch (err) {
      console.error("Kandidat löschen fehlgeschlagen", err);
      if (!silent) {
        setToast({ type: "error", msg: err?.message || "Löschen fehlgeschlagen." });
      }
      return false;
    } finally {
      setDeletingVersionId(null);
    }
  };

  // Archiv leeren: alles löschen, was gelöscht werden darf. Gesendete
  // Kandidaten bleiben stehen und werden am Ende gezählt gemeldet.
  const handleClearArchive = async () => {
    setClearingArchive(true);
    setToast(null);
    const list = deletableArchive;
    let done = 0;
    let failed = 0;
    try {
      for (let i = 0; i < list.length; i++) {
        setClearProgress(`${i + 1}/${list.length}`);
        // eslint-disable-next-line no-await-in-loop
        const ok = await handleDeleteCandidate(list[i], true);
        if (ok) done += 1; else failed += 1;
      }
      const kept = tabs.archive.length - list.length;
      let msg = `${done} Kandidat(en) gelöscht.`;
      if (failed) msg += ` ${failed} fehlgeschlagen.`;
      if (kept) msg += ` ${kept} als Historie behalten (waren beim Kunden).`;
      setToast({ type: failed ? "error" : "ok", msg });
    } finally {
      setClearingArchive(false);
      setClearProgress("");
    }
  };

  // Der Kandidat der Prüfansicht kommt live aus der Liste, damit ein
  // Statuswechsel (Freigabe, Ablehnung, Ersetzung durch den Renderer)
  // sofort ankommt.
  const compareCandidate = compareId
    ? candidates.find((c) => c.id === compareId) || null
    : null;
  const handleCloseCompare = useCallback(() => setCompareId(null), []);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "1em" }}>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        flexWrap: "wrap", gap: "1em",
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.5rem", color: C.textStrong }}>
            Auto-Entwürfe
          </h2>
          <div style={{ fontSize: "0.83rem", color: C.textMuted, marginTop: "0.2em" }}>
            Automatisch generierte Entwürfe prüfen und an Kunden senden ·
            Live-Update
          </div>
        </div>
      </div>

      {toast && (
        <div style={{
          marginTop: "1em", padding: "0.6em 0.9em", borderRadius: 6,
          fontSize: "0.85rem",
          background: toast.type === "ok" ? C.greenSoft : C.redSoft,
          color: toast.type === "ok" ? "#22543d" : "#742a2a",
          border: `1px solid ${toast.type === "ok" ? "#68d391" : "#fc8181"}`,
        }}>{toast.msg}</div>
      )}

      {loading ? (
        <div style={{ color: C.textMuted, padding: "2em 0" }}>Lade Kandidaten…</div>
      ) : (
        <>
          {/* Reiter */}
          <div style={{
            display: "flex", gap: "0.4em", flexWrap: "wrap",
            margin: "1.2em 0 1em", borderBottom: `1px solid ${C.border}`,
            paddingBottom: "0.6em",
          }}>
            {TABS.map(({ key, label }) => {
              const n = tabs[key].length;
              const active = key === activeTab;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    padding: "0.4em 0.9em", borderRadius: 999,
                    border: `1px solid ${active ? C.primaryDark : C.border}`,
                    background: active ? C.primaryDark : "#fff",
                    color: active ? "#fff" : C.textStrong,
                    fontSize: "0.85rem", fontWeight: active ? 700 : 500,
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {label}
                  <span style={{
                    marginLeft: "0.45em", fontSize: "0.78rem",
                    opacity: active ? 0.85 : 0.6,
                  }}>{n}</span>
                </button>
              );
            })}
          </div>

          {activeTab === "archive" && tabs.archive.length > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: "0.8em",
              flexWrap: "wrap", marginBottom: "0.8em",
            }}>
              <span style={{ fontSize: "0.8rem", color: C.textMuted }}>
                {deletableArchive.length} von {tabs.archive.length} löschbar —
                gesendete Entwürfe bleiben als Beleg erhalten.
              </span>
              {deletableArchive.length > 0 && (
                clearingArchive ? (
                  <span style={{ fontSize: "0.8rem", color: C.textMuted }}>
                    räume auf… ({clearProgress})
                  </span>
                ) : askClear ? (
                  <>
                    <span style={{ fontSize: "0.8rem", color: "#742a2a" }}>
                      {deletableArchive.length} Einträge endgültig löschen?
                    </span>
                    <button
                      onClick={() => { setAskClear(false); handleClearArchive(); }}
                      style={{
                        border: "none", borderRadius: 6, padding: "0.3em 0.8em",
                        background: C.red, color: "#fff", fontWeight: 600,
                        fontSize: "0.8rem", cursor: "pointer",
                      }}
                    >Ja, Archiv leeren</button>
                    <button
                      onClick={() => setAskClear(false)}
                      style={{
                        border: `1px solid ${C.border}`, borderRadius: 6,
                        padding: "0.3em 0.8em", background: "#fff",
                        color: C.textStrong, fontSize: "0.8rem", cursor: "pointer",
                      }}
                    >Abbrechen</button>
                  </>
                ) : (
                  <button
                    onClick={() => setAskClear(true)}
                    style={{
                      border: `1px solid ${C.border}`, borderRadius: 6,
                      padding: "0.3em 0.8em", background: "#fff",
                      color: C.red, fontSize: "0.8rem", cursor: "pointer",
                    }}
                  >Archiv leeren</button>
                )
              )}
            </div>
          )}

          {tabs[activeTab].length === 0 ? (
            <div style={{
              color: C.textMuted, fontSize: "0.88rem", padding: "1em",
              background: C.greySoft, borderRadius: 8,
            }}>{EMPTY_HINT[activeTab]}</div>
          ) : activeTab === "archive" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5em" }}>
              {tabs.archive.map((c) => (
                <CardErrorBoundary key={c.id} candidateId={c.id} orderId={c.orderId}>
                  <ArchiveEntry cand={c} sent={sentMap[c.id]}
                    onDelete={handleDeleteCandidate}
                    deletingId={deletingVersionId} />
                </CardErrorBoundary>
              ))}
            </div>
          ) : activeTab === "manual" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.7em" }}>
              {tabs.manual.map((c) => (
                <CardErrorBoundary key={c.id} candidateId={c.id} orderId={c.orderId}>
                  <ManualCard cand={c} order={orders[c.orderId]}
                    orderDrafts={draftsByOrder[c.orderId]}
                    history={historyByHead[c.id]}
                    onForce={handleForceAuto}
                    onCancelForce={handleCancelForceAuto}
                    busy={forcingId === c.id} />
                </CardErrorBoundary>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.9em" }}>
              {tabs[activeTab].map((c) => (
                <CardErrorBoundary key={c.id} candidateId={c.id} orderId={c.orderId}>
                  <CandidateCard cand={c}
                    order={orders[c.orderId]}
                    orderLoading={!!ordersLoading[c.orderId]}
                    onApprove={handleApprove} onReject={handleReject}
                    busyId={busyId}
                    draft={drafts[c.id]} draftLoading={!!draftsLoading[c.id]}
                    orderDrafts={draftsByOrder[c.orderId]}
                    history={historyByHead[c.id]} />
                </CardErrorBoundary>
              ))}
            </div>
          )}
        </>
      )}

      {/* Prüfansicht — interne Ansicht mit den Referenzbildern der
          Grafikerin. Sie verlässt diesen Overlay nicht. */}
      {compareCandidate && (
        <CompareView
          cand={compareCandidate}
          order={orders[compareCandidate.orderId]}
          orderLoading={!!ordersLoading[compareCandidate.orderId]}
          onApprove={handleApprove}
          onReject={handleReject}
          busyId={busyId}
          toast={toast}
          onClose={handleCloseCompare}
        />
      )}
    </div>
  );
};

export default DraftCandidatesPage;
